import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  QuestionnaireQuestionDefinition,
  QuestionnaireSectionDefinition,
  RuleExpression,
} from '@artem-lex/common-server/questionnaire/conditional'
import type {
  ComplianceDocumentKind,
  ComplianceDocumentManifest,
  ComplianceDocumentsLocaleKey,
  ComplianceDocumentRewriteDocumentType,
  ComplianceDocumentRewriteOutcome,
  ComplianceDocumentSectionKind,
  Frequency13849,
  PlrAssessment13849,
  PlrAssessmentOverride,
  PlrReviewReason,
  PlrReviewStatus,
  Possibility13849,
  SafetyFunctionPlrUndeterminedReason,
  Severity13849,
} from '@artem-lex/compliance-documents'
import {
  REWRITE_GATE_UNAVAILABLE_ERROR_CODE,
  REWRITE_INSUFFICIENT_SOURCES_ERROR_CODE,
  REWRITE_INSTRUCTION_REFUSED_ERROR_CODE,
  REWRITE_INVALID_OUTPUT_ERROR_CODE,
} from '@artem-lex/compliance-documents'
import { apiClient } from '@/shared/api/apiClient'
import { isAbortOrCancelError } from '@/shared/lib/is-abort-or-cancel-error'
import {
  mapSource,
  PRODUCT_SOURCES_KEY,
  type ProductSource,
} from '@/shared/hooks/api/productSource'
import type { UserStandard } from '@/shared/hooks/api/userStandards'
import {
  WorkflowEventType,
  applyWorkflowPollBatchEventsSequential,
  DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
  getLatestWorkflowForEntity,
  getWorkflowStatus,
  cancelWorkflow,
  type LatestWorkflowForEntityResponse,
  type WorkflowEvent,
} from '@/shared/hooks/api/workflows'
import {
  parseRewriteSseBuffer,
  type RewriteInstructionMutationResult,
} from '@/shared/hooks/api/rewriteInstructionStream'
import { resolveLocalizedHazardField } from '@/shared/lib/standardEntityLocalizations'
import {
  buildInstructionHandbookPdfFilename,
  buildRiskAssessmentPdfFilename,
} from '@/shared/utils/pdf-export-filenames'

export {
  WorkflowEventType,
  DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
  applyWorkflowPollBatchEventsSequential,
  type WorkflowEvent,
} from '@/shared/hooks/api/workflows'

export type {
  Frequency13849,
  PlrAssessment13849,
  PlrAssessmentOverride,
  PlrReviewReason,
  PlrReviewStatus,
  Possibility13849,
  SafetyFunctionPlrUndeterminedReason,
  Severity13849,
} from '@artem-lex/compliance-documents'

// --- Risk Assessment Document ---

export const RISK_ASSESSMENT_DOCUMENT_KEY =
  'machineryDirectiveComplianceRiskAssessmentDocument'

export type RiskAssessmentSectionRecord = {
  sectionId: string
  parentSectionId?: string | null
  title: string
  kind: 'canonical' | 'custom'
  sortOrder: number
  markdown: string
  generatedAt?: string
}

export type RiskAssessmentDocument = {
  id?: string
  machineryDirectiveComplianceId?: string
  sections: RiskAssessmentSectionRecord[]
  version?: string | null
  versionNumber?: number
  language?: string
  createdAt?: string
  generatedAt?: string
  notSpecifiedCount?: number | null
  notSpecifiedItems?: NotSpecifiedItem[] | null
  isSection8Expired?: boolean
}

/** SSE event types for risk assessment stream (must match server) */
export type RiskAssessmentStreamEvent =
  | { type: 'document_section.started'; section: string; title?: string }
  | { type: 'document_section.progress'; section?: string; text: string }
  | { type: 'document_section.completed'; section: string }
  | {
      type: 'document.completed'
      markdown: string
      version?: string
      versionNumber?: number
      notSpecifiedCount?: number
      notSpecifiedItems?: NotSpecifiedItem[]
    }
  | { type: 'error'; error: string }

// --- Instruction Handbook (Manual Generation) Document ---

export const INSTRUCTION_HANDBOOK_DOCUMENT_KEY =
  'machineryDirectiveComplianceInstructionHandbookDocument'

export type NotSpecifiedItem = {
  text: string
  fieldLabel: string | null
  guidance: string | null
  type: 'not_found' | 'not_specified' | 'not_found_in_documents'
}

export type InstructionHandbookSectionRecord = {
  sectionId: string
  parentSectionId?: string | null
  title: string
  kind: 'canonical' | 'custom'
  sortOrder: number
  markdown: string
  generatedAt?: string
}

export type InstructionHandbookDocument = {
  id?: string
  machineryDirectiveComplianceId?: string
  sections: InstructionHandbookSectionRecord[]
  version?: string | null
  versionNumber?: number
  language?: string
  createdAt?: string
  generatedAt?: string
  notSpecifiedCount?: number | null
  notSpecifiedItems?: NotSpecifiedItem[] | null
}

/** SSE event types for instruction handbook stream (same shape as risk assessment) */
export type InstructionHandbookStreamEvent =
  | { type: 'document_section.started'; section: string; title?: string }
  | { type: 'document_section.progress'; section?: string; text: string }
  | { type: 'document_section.completed'; section: string }
  | {
      type: 'document.completed'
      markdown: string
      sections?: InstructionHandbookSectionRecord[]
      version?: string
      versionNumber?: number
      notSpecifiedCount?: number
      notSpecifiedItems?: NotSpecifiedItem[]
    }
  | { type: 'error'; error: string }

export type MachineryDirectiveCompliance = {
  id: string
  productId?: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  highRiskCheckedAt?: string | null
  isHighRisk?: boolean | null
  highRiskCategory?: string | null
  highRiskReasoning?: string | null
  highRiskUserOverrideReason?: string | null
  highRiskUserOverrideAt?: string | null
  isPartlyCompletedMachine?: boolean | null
  isPartlyCompletedMachineUpdatedAt?: string | null
  machineCharacteristicsAnswersJson?: Record<
    string,
    boolean | string | null
  > | null
  machineCharacteristicsTemplateVersion?: string | null
  machineCharacteristicsMappingVersion?: string | null
  machineCharacteristicsApplicableQuestionKeysJson?: string[] | null
  machineCharacteristicsExtractionEvidenceJson?: Record<
    string,
    {
      rationale: string
      sourceFilename: string | null
      excerpt: string | null
    } | null
  > | null
  machineCharacteristicsExtractionCompletedAt?: string | null
  typeBQuestionnaireGeneratedAt?: string | null
  typeBQuestionnaireGenerationFingerprint?: string | null
  completed?: boolean
  completedAt?: string | null
  completedByActor?: 'OEM' | 'SUPPLIER' | null
  declarationOfConformityMarkdown?: string | null
  declarationOfConformityUpdatedAt?: string | null
  contentLanguage: ComplianceDocumentsLocaleKey
}

const MACHINERY_DIRECTIVE_COMPLIANCE_KEY = 'machineryDirectiveCompliance'
export const MACHINERY_DIRECTIVE_E_PLAN_KEY = 'machineryDirectiveEPlan' as const

export type MachineryDirectiveEPlanResponse = {
  productSource: ProductSource | null
}

export const COMPLIANCE_DOCUMENT_MANIFEST_KEY = 'complianceDocumentManifest'

export type { ComplianceDocumentKind, ComplianceDocumentManifest }

export function useComplianceDocumentManifest(
  documentKind: ComplianceDocumentKind | undefined,
  locale: string | undefined
) {
  return useQuery<ComplianceDocumentManifest, Error>({
    queryKey: [COMPLIANCE_DOCUMENT_MANIFEST_KEY, documentKind, locale],
    queryFn: async () => {
      if (!documentKind) {
        throw new Error('documentKind is required')
      }
      const params = new URLSearchParams({ documentKind })
      if (locale) {
        params.set('locale', locale)
      }
      const response = await apiClient.get<ComplianceDocumentManifest>(
        `/machinery-directive-compliance/document-manifest?${params.toString()}`
      )
      return response.data
    },
    enabled: documentKind != null,
    staleTime: 24 * 60 * 60 * 1000,
  })
}

export function useGetMachineryDirectiveCompliance(
  complianceId: string | undefined
) {
  return useQuery<MachineryDirectiveCompliance, Error>({
    queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<MachineryDirectiveCompliance>(
        `/machinery-directive-compliance/${complianceId}`
      )
      return response.data
    },
    enabled: !!complianceId,
  })
}

export function useUpdateMachineryDirectiveComplianceCompleted(
  complianceId: string | undefined
) {
  const queryClient = useQueryClient()
  return useMutation<
    MachineryDirectiveCompliance & { warnings?: string[] },
    Error,
    { completed: boolean }
  >({
    mutationFn: async ({ completed }) => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.patch<
        MachineryDirectiveCompliance & { warnings?: string[] }
      >(`/machinery-directive-compliance/${complianceId}`, { completed })
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

export function useUpdatePartlyCompletedMachine(
  complianceId: string | undefined
) {
  const queryClient = useQueryClient()
  return useMutation<
    MachineryDirectiveCompliance,
    Error,
    { isPartlyCompletedMachine: boolean }
  >({
    mutationFn: async ({ isPartlyCompletedMachine }) => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.patch<MachineryDirectiveCompliance>(
        `/machinery-directive-compliance/${complianceId}/partly-completed-machine`,
        { isPartlyCompletedMachine }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

export type TypeBQuestionnaireRuleExpression = RuleExpression
export type TypeBQuestionnaireSection = QuestionnaireSectionDefinition
export type TypeBQuestionnaireQuestion = QuestionnaireQuestionDefinition

export type TypeBQuestionnaireDefinition = {
  templateKey: string
  templateVersion: string
  mappingVersion: string
  sections: TypeBQuestionnaireSection[]
  questions: TypeBQuestionnaireQuestion[]
  enums: {
    machine_mobility: string[]
  }
}

export type TypeBQuestionnaireCompletionState = {
  templateVersion: string
  mappingVersion: string
  visibleQuestionKeys: string[]
  missingRequiredQuestionKeys: string[]
  isComplete: boolean
}

export type TypeBQuestionnaireExtractionEvidenceEntry = {
  rationale: string
  sourceFilename: string | null
  excerpt: string | null
}

export type TypeBQuestionnaireStateResponse = {
  definition: TypeBQuestionnaireDefinition
  answers: Record<string, boolean | string | null>
  completion: TypeBQuestionnaireCompletionState
  extractionEvidence: Record<
    string,
    TypeBQuestionnaireExtractionEvidenceEntry | null
  >
  generatedAt: string | null
  generationFingerprint: string | null
  isGenerated: boolean
  isStale: boolean
}

export type TypeBQuestionnaireGenerateResponse = {
  candidatesCount: number
  references: string[]
  completion: TypeBQuestionnaireCompletionState
  generationFingerprint: string
}

const TYPE_B_QUESTIONNAIRE_KEY = 'type-b-questionnaire'

const MACHINE_CHARACTERISTICS_EXTRACTION_POLL_MS = 2000
const MACHINE_CHARACTERISTICS_EXTRACTION_MAX_WAIT_MS = 15 * 60 * 1000

export function useGetTypeBQuestionnaire(complianceId: string | undefined) {
  return useQuery<TypeBQuestionnaireStateResponse, Error>({
    queryKey: [TYPE_B_QUESTIONNAIRE_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<TypeBQuestionnaireStateResponse>(
        `/machinery-directive-compliance/${complianceId}/type-b-questionnaire`
      )
      return response.data
    },
    enabled: !!complianceId,
  })
}

export function usePatchTypeBQuestionnaireAnswers(
  complianceId: string | undefined
) {
  const queryClient = useQueryClient()
  return useMutation<
    TypeBQuestionnaireStateResponse,
    Error,
    { answers: Record<string, unknown> }
  >({
    mutationFn: async ({ answers }) => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.patch<TypeBQuestionnaireStateResponse>(
        `/machinery-directive-compliance/${complianceId}/type-b-questionnaire/answers`,
        { answers }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TYPE_B_QUESTIONNAIRE_KEY, complianceId],
      })
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

export function useGenerateTypeBQuestionnaireCandidates(
  complianceId: string | undefined
) {
  const queryClient = useQueryClient()
  return useMutation<TypeBQuestionnaireGenerateResponse, Error, void>({
    mutationFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.post<TypeBQuestionnaireGenerateResponse>(
        `/machinery-directive-compliance/${complianceId}/type-b-questionnaire/generate`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TYPE_B_QUESTIONNAIRE_KEY, complianceId],
      })
      void queryClient.invalidateQueries({
        queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
      })
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

export function useSetHighRiskUserOverrideReason(
  complianceId: string | undefined
) {
  const queryClient = useQueryClient()
  return useMutation<
    MachineryDirectiveCompliance,
    Error,
    { reason: string | null }
  >({
    mutationFn: async ({ reason }) => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.patch<MachineryDirectiveCompliance>(
        `/machinery-directive-compliance/${complianceId}/high-risk-user-override`,
        { reason }
      )
      return response.data
    },
    onSuccess: (data) => {
      if (complianceId) {
        queryClient.setQueryData(
          [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
          data
        )
      }
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

export function useGetMachineryDirectiveEPlan(
  complianceId: string | undefined
) {
  return useQuery<MachineryDirectiveEPlanResponse, Error>({
    queryKey: [MACHINERY_DIRECTIVE_E_PLAN_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<{
        productSource: Record<string, unknown> | null
      }>(`/machinery-directive-compliance/${complianceId}/e-plan`)
      const productSource = response.data.productSource
      return {
        productSource: productSource != null ? mapSource(productSource) : null,
      }
    },
    enabled: Boolean(complianceId),
    refetchInterval(query) {
      const productSource = query.state.data?.productSource
      if (productSource == null) {
        return false
      }
      if (productSource.fileUploadStatus === 'PENDING') {
        return 5000
      }
      if (
        productSource.ingestionStatus === 'PROCESSING' ||
        (productSource.ingestionStatus === 'PENDING' &&
          productSource.fileUploadStatus !== 'FAILED')
      ) {
        return 5000
      }
      return false
    },
  })
}

export function useUpdateMachineryDirectiveEPlan(
  complianceId: string | undefined,
  productId?: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    MachineryDirectiveEPlanResponse,
    Error,
    { productSourceId: string }
  >({
    mutationFn: async ({ productSourceId }) => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.put<{
        productSource: Record<string, unknown>
      }>(`/machinery-directive-compliance/${complianceId}/e-plan`, {
        productSourceId,
      })
      return {
        productSource: mapSource(response.data.productSource),
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_E_PLAN_KEY, complianceId],
      })
      if (productId != null) {
        void queryClient.invalidateQueries({
          queryKey: [PRODUCT_SOURCES_KEY, productId],
        })
      }
    },
  })
}

export function useClearMachineryDirectiveEPlan(
  complianceId: string | undefined,
  productId?: string
) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      await apiClient.delete(
        `/machinery-directive-compliance/${complianceId}/e-plan`
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_E_PLAN_KEY, complianceId],
      })
      if (productId != null) {
        void queryClient.invalidateQueries({
          queryKey: [PRODUCT_SOURCES_KEY, productId],
        })
      }
    },
  })
}

const STANDARD_APPLICABILITY_DISCOVERY_POLL_MS = 2000
const STANDARD_APPLICABILITY_DISCOVERY_MAX_WAIT_MS = 15 * 60 * 1000

/**
 * Latest persisted standard applicability discovery workflow for polling step / LLM events.
 * Poll while mounted until the run completes, fails, or none exists.
 */
export function useStandardApplicabilityDiscoveryWorkflowLatest(
  complianceId: string | undefined,
  enabled = true
) {
  return useQuery<LatestWorkflowForEntityResponse, Error>({
    queryKey: [
      MACHINERY_DIRECTIVE_COMPLIANCE_KEY,
      complianceId,
      'standard-applicability-discovery-workflow-latest',
    ],
    queryFn: async () =>
      getLatestWorkflowForEntity(
        'machinery_directive_compliance',
        complianceId!,
        'standard_applicability_discovery'
      ),
    enabled: Boolean(complianceId) && enabled,
    refetchInterval(query) {
      const status = query.state.data?.status
      if (
        status === 'failed' ||
        status === 'completed' ||
        status === 'cancelled' ||
        status === 'none'
      ) {
        return false
      }
      return STANDARD_APPLICABILITY_DISCOVERY_POLL_MS
    },
    staleTime: 0,
  })
}

export type StandardApplicability = {
  id: string
  machineryDirectiveComplianceId: string
  reference: string
  title: string
  type: string
  applicability: string
  certainty: string
  explanation: string
  isAccepted: boolean | null
  source: string
  userStandardId: string | null
  inEffect?: boolean | null
  hasFile?: boolean
  hasSignificantHazards?: boolean
  hasInformationForUse?: boolean
  hasRiskReductionMeasures?: boolean
  createdAt: string
  updatedAt: string
}

const STANDARD_APPLICABILITY_KEY = 'standard-applicability'

export function useListStandardApplicability(complianceId?: string) {
  return useQuery<StandardApplicability[], Error>({
    queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
    queryFn: async () => {
      const response = await apiClient.get<StandardApplicability[]>(
        `/machinery-directive-compliance/${complianceId}/standard-applicability`
      )
      return response.data
    },
    enabled: !!complianceId,
  })
}

export function useAddStandardApplicability(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<StandardApplicability, Error, { userStandardId: string }>({
    mutationFn: async ({ userStandardId }) => {
      const response = await apiClient.post<StandardApplicability>(
        `/machinery-directive-compliance/${complianceId}/standard-applicability`,
        { userStandardId }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
      })
    },
  })
}

export type StandardApplicabilityDecisionPatch = {
  standardId: string
  isAccepted: boolean
}

async function patchStandardApplicabilityDecisionsBatch(
  complianceId: string,
  patches: StandardApplicabilityDecisionPatch[]
): Promise<StandardApplicability[]> {
  const response = await apiClient.patch<StandardApplicability[]>(
    `/machinery-directive-compliance/${complianceId}/standard-applicability/decisions`,
    { decisions: patches }
  )
  return response.data
}

async function patchStandardApplicabilityDecision(
  complianceId: string,
  { standardId, isAccepted }: StandardApplicabilityDecisionPatch
): Promise<StandardApplicability> {
  const response = await apiClient.patch<StandardApplicability>(
    `/machinery-directive-compliance/${complianceId}/standard-applicability/${standardId}`,
    { isAccepted }
  )
  return response.data
}

/** Rows where desired acceptance differs from persisted `isAccepted` (null always patches). */
export function buildStandardApplicabilityDecisionPatches(
  standards: StandardApplicability[],
  selectedStandardIds: Set<string>
): StandardApplicabilityDecisionPatch[] {
  const patches: StandardApplicabilityDecisionPatch[] = []
  for (const standard of standards) {
    const desiredAccepted = selectedStandardIds.has(standard.id)
    if (standard.isAccepted === null) {
      patches.push({ standardId: standard.id, isAccepted: desiredAccepted })
      continue
    }
    if (standard.isAccepted !== desiredAccepted) {
      patches.push({ standardId: standard.id, isAccepted: desiredAccepted })
    }
  }
  return patches
}

export function useUpdateStandardApplicabilityDecision(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    StandardApplicability,
    Error,
    StandardApplicabilityDecisionPatch
  >({
    mutationFn: async (patch) => {
      return patchStandardApplicabilityDecision(complianceId, patch)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
    },
  })
}

export function useBatchUpdateStandardApplicabilityDecisions(
  complianceId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    StandardApplicability[],
    Error,
    StandardApplicabilityDecisionPatch[]
  >({
    mutationFn: async (patches) => {
      if (patches.length === 0) {
        return []
      }
      return patchStandardApplicabilityDecisionsBatch(complianceId, patches)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
    },
  })
}

/** Preview item for hazard generation modal (id used as selectedHazardIds). */
export type StandardsHazardPreviewItem = {
  id: string
  index: number
  standardReference: string
  number: string
  description: string
  category?: string
  examplesOfHazardousSituations?: string
  associatedActivity?: string
  relatedDangerZone?: string
  clauseReference?: string
}

/**
 * Builds the flat list of hazard items from standards in the same order as the server collect step;
 * items include id for selectedHazardIds.
 */
export function buildStandardsHazardPreviewItems(
  standards: Pick<UserStandard, 'reference' | 'significantHazards'>[],
  referencesOrder: string[],
  displayLocale?: ComplianceDocumentsLocaleKey
): StandardsHazardPreviewItem[] {
  const byRef = new Map(
    standards.map((standard) => [standard.reference, standard])
  )
  const items: StandardsHazardPreviewItem[] = []
  let index = 0
  for (const ref of referencesOrder) {
    const standard = byRef.get(ref)
    if (
      !standard?.significantHazards?.hazards ||
      !Array.isArray(standard.significantHazards.hazards)
    ) {
      continue
    }
    for (const hazard of standard.significantHazards.hazards) {
      const description =
        displayLocale != null
          ? resolveLocalizedHazardField(hazard, displayLocale, 'description')
          : hazard.description
      if (description) {
        const hazardId =
          hazard.id != null && String(hazard.id).trim() !== ''
            ? String(hazard.id).trim()
            : ''
        const categoryRaw =
          displayLocale != null
            ? resolveLocalizedHazardField(hazard, displayLocale, 'category')
            : hazard.category
        const category = categoryRaw?.trim() ?? ''
        const examplesRaw =
          displayLocale != null
            ? resolveLocalizedHazardField(
                hazard,
                displayLocale,
                'examplesOfHazardousSituations'
              )
            : hazard.examplesOfHazardousSituations
        const associatedActivityRaw =
          displayLocale != null
            ? resolveLocalizedHazardField(
                hazard,
                displayLocale,
                'associatedActivity'
              )
            : hazard.associatedActivity
        const relatedDangerZoneRaw =
          displayLocale != null
            ? resolveLocalizedHazardField(
                hazard,
                displayLocale,
                'relatedDangerZone'
              )
            : hazard.relatedDangerZone
        const examplesOfHazardousSituations =
          examplesRaw != null && examplesRaw.trim() !== ''
            ? examplesRaw.trim()
            : undefined
        const associatedActivity =
          associatedActivityRaw != null && associatedActivityRaw.trim() !== ''
            ? associatedActivityRaw.trim()
            : undefined
        const relatedDangerZone =
          relatedDangerZoneRaw != null && relatedDangerZoneRaw.trim() !== ''
            ? relatedDangerZoneRaw.trim()
            : undefined
        const clauseReferenceRaw = hazard.clauseReference
        const clauseReference =
          clauseReferenceRaw != null && String(clauseReferenceRaw).trim() !== ''
            ? String(clauseReferenceRaw).trim()
            : undefined
        items.push({
          id: hazardId,
          index,
          standardReference: ref,
          number: hazard.number ?? '',
          description,
          category,
          examplesOfHazardousSituations,
          associatedActivity,
          relatedDangerZone,
          clauseReference,
        })
        index++
      }
    }
  }
  return items
}

// --- Hazard Identification ---

export type HazardIdentification = {
  id: string
  machineryDirectiveComplianceId: string
  riskIn: string | null
  riskInInjury: string | null
  riskInFrequency: string | null
  riskInAvoidance: string | null
  riskInLikelihood: string | null
  riskInInjuryJustification?: string | null
  riskInFrequencyJustification?: string | null
  riskInAvoidanceJustification?: string | null
  riskInLikelihoodJustification?: string | null
  riskInScore: number
  machineLocationId?: string | null
  type: string
  lifecycles: string[]
  description: string
  reasoning: string | null
  source: 'USER' | 'AGENT'
  significantHazardId?: string | null
  sourceStandardReference?: string | null
  sourceClauseReference?: string | null
  pictogramId?: string | null
  ehsrClauseIds?: string[] | null
  uncertain: boolean | null
  riskAdequatelyReducedAt: string | null
  createdAt: string
  updatedAt: string
  hazardRiskReductionStepsCount: number
  finalRiskOutScore?: number | null
}

/** SSE event types for hazard generation stream (must match server) */
export type HazardGenerationStreamEvent =
  | { type: 'workflow.started'; workflowRunId: string; workflowType: string }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string }
  | { type: 'progress'; message: string; temporary?: true }
  | {
      type: 'hazard.detected'
      hazard: {
        machineLocationId: string
        type: string | null
        lifecycles: string[]
        description: string | null
      }
      reasoning: string
      standardHazard: { number: string; description: string; category?: string }
      hazardIndex: number
      totalHazards: number
    }
  | {
      type: 'hazard.created'
      hazardId: string
      hazardIndex: number
      totalHazards: number
      uncertain?: boolean | null
    }
  | { type: 'workflow.completed'; workflowRunId: string; result: unknown }
  | { type: 'workflow.failed'; workflowRunId: string; error: string }
  | { type: 'error'; error: string; step?: string }

const HAZARD_IDENTIFICATIONS_KEY = 'hazard-identifications'

export const EHSR_CLAUSE_LIST_KEY = 'machineryDirectiveEhsrClauseList'

export type EhsrClauseApiEntry = {
  id: string
  annex: string
  clauseNumber: string
}

/** Static EHSR clause ids from the server (labels are localized client-side). */
export function useMachineryDirectiveEhsrClauseList() {
  return useQuery<EhsrClauseApiEntry[], Error>({
    queryKey: [EHSR_CLAUSE_LIST_KEY],
    queryFn: async () => {
      const response = await apiClient.get<EhsrClauseApiEntry[]>(
        '/machinery-directive-compliance/ehsr-list'
      )
      return response.data
    },
    staleTime: 24 * 60 * 60 * 1000,
  })
}

export function useListHazardIdentifications(complianceId: string | undefined) {
  return useQuery<HazardIdentification[], Error>({
    queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<HazardIdentification[]>(
        `/machinery-directive-compliance/${complianceId}/hazards`
      )
      return response.data
    },
    enabled: !!complianceId,
  })
}

export function useGetHazardIdentification(
  complianceId: string | undefined,
  hazardId: string | undefined
) {
  return useQuery<HazardIdentification | null, Error>({
    queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId, hazardId],
    queryFn: async () => {
      if (!complianceId || !hazardId) {
        throw new Error('Compliance ID and hazard ID are required')
      }
      const response = await apiClient.get<HazardIdentification>(
        `/machinery-directive-compliance/${complianceId}/hazards/${hazardId}`
      )
      return response.data
    },
    enabled: !!complianceId && !!hazardId,
  })
}

export type CreateHazardIdentificationPayload = {
  riskInInjury?: string | null
  riskInFrequency?: string | null
  riskInAvoidance?: string | null
  riskInLikelihood?: string | null
  riskInInjuryJustification?: string | null
  riskInFrequencyJustification?: string | null
  riskInAvoidanceJustification?: string | null
  riskInLikelihoodJustification?: string | null
  machineLocationId?: string | null
  type?: string | null
  lifecycles?: string[]
  description?: string | null
  reasoning?: string | null
  source?: 'USER' | 'AGENT'
  pictogramId?: string | null
  ehsrClauseIds?: string[] | null
}

export function useCreateHazardIdentification(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    HazardIdentification,
    Error,
    CreateHazardIdentificationPayload
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.post<HazardIdentification>(
        `/machinery-directive-compliance/${complianceId}/hazards`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
      })
    },
  })
}

export type UpdateHazardIdentificationPayload = {
  riskInInjury?: string | null
  riskInFrequency?: string | null
  riskInAvoidance?: string | null
  riskInLikelihood?: string | null
  riskInInjuryJustification?: string | null
  riskInFrequencyJustification?: string | null
  riskInAvoidanceJustification?: string | null
  riskInLikelihoodJustification?: string | null
  machineLocationId?: string | null
  type?: string | null
  lifecycles?: string[]
  description?: string | null
  reasoning?: string | null
  source?: 'USER' | 'AGENT'
  riskAdequatelyReducedAt?: string | null
  pictogramId?: string | null
  ehsrClauseIds?: string[] | null
}

export function useUpdateHazardIdentification(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    HazardIdentification,
    Error,
    { hazardId: string } & UpdateHazardIdentificationPayload
  >({
    mutationFn: async ({ hazardId, ...payload }) => {
      const response = await apiClient.patch<HazardIdentification>(
        `/machinery-directive-compliance/${complianceId}/hazards/${hazardId}`,
        payload
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [HAZARD_RISK_REDUCTION_STEPS_KEY, variables.hazardId],
      })
      invalidateAllSafetyFunctions(queryClient, complianceId)
    },
  })
}

export function useToggleHazardIdentificationRiskAdequatelyReduced(
  complianceId: string
) {
  const queryClient = useQueryClient()
  return useMutation<HazardIdentification, Error, string>({
    mutationFn: async (hazardId: string) => {
      const response = await apiClient.post<HazardIdentification>(
        `/machinery-directive-compliance/${complianceId}/hazards/${hazardId}/risk-adequately-reduced`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
      })
    },
  })
}

export function useApproveHazardIdentification(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<HazardIdentification, Error, string>({
    mutationFn: async (hazardId) => {
      const response = await apiClient.post<HazardIdentification>(
        `/machinery-directive-compliance/${complianceId}/hazards/${hazardId}/approve`
      )
      return response.data
    },
    onSuccess: (_data, hazardId) => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId, hazardId],
      })
    },
  })
}

export function useDeleteHazardIdentification(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (hazardId) => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/machinery-directive-compliance/${complianceId}/hazards/${hazardId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
      invalidateAllSafetyFunctions(queryClient, complianceId)
    },
  })
}

// --- Risk Reduction Measures ---

export type RiskReductionMeasureType = 'ISD' | 'SCP' | 'PPE' | 'OI'

export type RiskReductionMeasurePerformanceLevel = 'A' | 'B' | 'C' | 'D' | 'E'

export type RiskReductionMeasureLinkedHazard = {
  id: string
  description: string | null
  type: string | null
}

export type RiskReductionMeasureImplementationStatus =
  | 'NOT_IMPLEMENTED'
  | 'IMPLEMENTED'

export type RiskReductionMeasureImplementationProofSource = {
  id: string
  name: string
  type: string
  ingestionStatus: string
}

export type RiskReductionMeasureStandardConformityStandard = {
  id: string
  reference: string
  title: string
}

export type RiskReductionMeasureStandardConformity = {
  note: string | null
  standards: RiskReductionMeasureStandardConformityStandard[]
}

export type RiskReductionMeasure = {
  id: string
  machineryDirectiveComplianceId: string
  name: string
  type: RiskReductionMeasureType
  description: string | null
  source: 'USER' | 'STANDARD'
  standardExtractedMeasureId: string | null
  sourceStandardReference?: string | null
  sourceClauseReference?: string | null
  dependsOnControlSystem: boolean | null
  safetyFunctionId: string | null
  ehsrClauseIds?: string[] | null
  implementationStatus: RiskReductionMeasureImplementationStatus
  implementationNote: string | null
  standardConformity: RiskReductionMeasureStandardConformity
  implementationProofSources: RiskReductionMeasureImplementationProofSource[]
  createdAt: string
  updatedAt: string
  plr: RiskReductionMeasurePerformanceLevel | null
  linkedHazards?: RiskReductionMeasureLinkedHazard[]
}

export type RiskReductionMeasureInput = {
  name: string
  type: RiskReductionMeasureType
  description: string
  source?: 'USER' | 'STANDARD'
  standardExtractedMeasureId?: string | null
  dependsOnControlSystem?: boolean | null
  safetyFunctionId?: string | null
  ehsrClauseIds?: string[] | null
  implementationStatus?: RiskReductionMeasureImplementationStatus
  implementationNote?: string | null
  conformityStandardApplicabilityIds?: string[]
  conformityNote?: string | null
}

export const RISK_REDUCTION_MEASURES_KEY = 'risk-reduction-measures'

/** SSE event types for RRM generation stream (must match server) */
export type RrmGenerationStreamEvent =
  | { type: 'workflow.started'; workflowRunId: string; workflowType: string }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string; result?: unknown }
  | { type: 'progress'; message: string; temporary?: true }
  | { type: 'llm.started'; step?: string }
  | {
      type: 'llm.completed'
      step?: string
      model?: string
      usage?: {
        promptTokens: number
        completionTokens: number
        totalTokens: number
      }
    }
  | {
      type: 'rrm.created'
      rrmId: string
      standardExtractedMeasureId: string
      rrmIndex: number
      totalRrms: number
    }
  | { type: 'workflow.completed'; workflowRunId: string; result: unknown }
  | { type: 'workflow.failed'; workflowRunId: string; error: string }
  | { type: 'error'; error: string; step?: string }

export type EplanSafetyFunctionExtractionResult = {
  proposals: EplanSafetyFunctionProposal[]
  cached?: boolean
  extractedAt?: string
  message?: string
  listingComplete?: boolean
  pendingBatchCount?: number
  incompleteArchitectureIds?: string[]
  manualReviewArchitectureIds?: string[]
}

/** SSE event types for E-plan safety function extraction stream */
export type EplanSafetyFunctionExtractionStreamEvent =
  | { type: 'workflow.started'; workflowRunId: string; workflowType: string }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string; result?: unknown }
  | { type: 'progress'; message: string; temporary?: true }
  | { type: 'llm.started'; step?: string; stepId?: string }
  | {
      type: 'llm.completed'
      step?: string
      stepId?: string
      usage?: {
        promptTokens: number
        completionTokens: number
        totalTokens: number
      }
    }
  | {
      type: 'safety_function.extracted'
      eplanExtractedFunctionId: string
      functionIndex: number
      totalFunctions: number
    }
  | {
      type: 'workflow.completed'
      workflowRunId: string
      result: EplanSafetyFunctionExtractionResult
    }
  | { type: 'workflow.failed'; workflowRunId: string; error: string }
  | { type: 'error'; error: string; step?: string }

/** SSE event types for E-plan safety function import stream */
export type EplanSafetyFunctionImportStreamEvent =
  | { type: 'workflow.started'; workflowRunId: string; workflowType: string }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string; result?: unknown }
  | { type: 'progress'; message: string; temporary?: true }
  | {
      type: 'safety_function.imported'
      safetyFunctionId: string
      eplanExtractedFunctionId: string
      functionIndex: number
      totalFunctions: number
    }
  | {
      type: 'workflow.completed'
      workflowRunId: string
      result: {
        safetyFunctionsCreated: number
        safetyFunctionsSkipped: number
        totalProcessed: number
        safetyFunctionIds: string[]
      }
    }
  | { type: 'workflow.failed'; workflowRunId: string; error: string }
  | { type: 'error'; error: string; step?: string }

export function useListRiskReductionMeasures(complianceId: string | undefined) {
  return useQuery<RiskReductionMeasure[], Error>({
    queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<RiskReductionMeasure[]>(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures`
      )
      return response.data
    },
    enabled: !!complianceId,
  })
}

export function useCreateRiskReductionMeasure(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<RiskReductionMeasure, Error, RiskReductionMeasureInput>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<RiskReductionMeasure>(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
      invalidateAllSafetyFunctions(queryClient, complianceId)
    },
  })
}

export function useGetRiskReductionMeasure(
  complianceId: string | undefined,
  riskReductionMeasureId: string | undefined
) {
  return useQuery<RiskReductionMeasure, Error>({
    queryKey: [
      RISK_REDUCTION_MEASURES_KEY,
      complianceId,
      riskReductionMeasureId,
    ],
    queryFn: async () => {
      if (!complianceId || !riskReductionMeasureId) {
        throw new Error(
          'Compliance ID and risk reduction measure ID are required'
        )
      }
      const response = await apiClient.get<RiskReductionMeasure>(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures/${riskReductionMeasureId}`
      )
      return response.data
    },
    enabled: !!complianceId && !!riskReductionMeasureId,
  })
}

export function useUpdateRiskReductionMeasure(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    RiskReductionMeasure,
    Error,
    { riskReductionMeasureId: string } & RiskReductionMeasureInput
  >({
    mutationFn: async ({ riskReductionMeasureId, ...payload }) => {
      const response = await apiClient.patch<RiskReductionMeasure>(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures/${riskReductionMeasureId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
      invalidateAllSafetyFunctions(queryClient, complianceId)
    },
  })
}

export function useDeleteRiskReductionMeasure(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (riskReductionMeasureId) => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures/${riskReductionMeasureId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
      invalidateAllSafetyFunctions(queryClient, complianceId)
    },
  })
}

export function useAttachRiskReductionMeasureImplementationProofSource(
  complianceId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    RiskReductionMeasure,
    Error,
    { riskReductionMeasureId: string; productSourceId: string }
  >({
    mutationFn: async ({ riskReductionMeasureId, productSourceId }) => {
      const response = await apiClient.post<RiskReductionMeasure>(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures/${riskReductionMeasureId}/implementation-proof-sources`,
        { productSourceId }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
    },
  })
}

export function useDetachRiskReductionMeasureImplementationProofSource(
  complianceId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    RiskReductionMeasure,
    Error,
    { riskReductionMeasureId: string; productSourceId: string }
  >({
    mutationFn: async ({ riskReductionMeasureId, productSourceId }) => {
      const response = await apiClient.delete<RiskReductionMeasure>(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures/${riskReductionMeasureId}/implementation-proof-sources/${productSourceId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
    },
  })
}

// --- Machine Locations ---

export const MACHINE_LOCATIONS_KEY = 'machine-locations'

export type MachineLocation = {
  id: string
  machineryDirectiveComplianceId: string
  name: string
  description: string
  reasoning: string | null
  source: string | null
  createdAt: string
  updatedAt: string
}

export type MachineLocationInput = {
  name: string
  description: string
}

export function useMachineLocations(complianceId: string) {
  return useQuery<MachineLocation[]>({
    queryKey: [MACHINE_LOCATIONS_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<MachineLocation[]>(
        `/machinery-directive-compliance/${complianceId}/machine-locations`
      )
      return response.data
    },
    enabled: !!complianceId,
  })
}

export function useCreateMachineLocation(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<MachineLocation, Error, MachineLocationInput>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<MachineLocation>(
        `/machinery-directive-compliance/${complianceId}/machine-locations`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MACHINE_LOCATIONS_KEY, complianceId],
      })
    },
  })
}

export function useUpdateMachineLocation(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    MachineLocation,
    Error,
    { machineLocationId: string } & MachineLocationInput
  >({
    mutationFn: async ({ machineLocationId, ...payload }) => {
      const response = await apiClient.patch<MachineLocation>(
        `/machinery-directive-compliance/${complianceId}/machine-locations/${machineLocationId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MACHINE_LOCATIONS_KEY, complianceId],
      })
    },
  })
}

export function useDeleteMachineLocation(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (machineLocationId) => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/machinery-directive-compliance/${complianceId}/machine-locations/${machineLocationId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MACHINE_LOCATIONS_KEY, complianceId],
      })
    },
  })
}

const MACHINE_LOCATION_GENERATION_POLL_MS = 2000
const MACHINE_LOCATION_GENERATION_MAX_WAIT_MS = 15 * 60 * 1000

export type MachineLocationGenerationResult = {
  createdCount?: number
  skippedDuplicateCount?: number
  skippedInvalidCount?: number
}

export function useMachineLocationGenerationWorkflowLatest(
  complianceId: string | undefined,
  enabled = true
) {
  return useQuery<LatestWorkflowForEntityResponse, Error>({
    queryKey: [
      MACHINERY_DIRECTIVE_COMPLIANCE_KEY,
      complianceId,
      'machine-location-generation-workflow-latest',
    ],
    queryFn: async () =>
      getLatestWorkflowForEntity(
        'machinery_directive_compliance',
        complianceId!,
        'machine_location_generation'
      ),
    enabled: Boolean(complianceId) && enabled,
    refetchInterval(query) {
      const status = query.state.data?.status
      if (
        status === 'failed' ||
        status === 'completed' ||
        status === 'cancelled' ||
        status === 'none'
      ) {
        return false
      }
      return MACHINE_LOCATION_GENERATION_POLL_MS
    },
    staleTime: 0,
  })
}

export function useStartMachineLocationGeneration(complianceId: string) {
  const queryClient = useQueryClient()
  const pollReleaseRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      pollReleaseRef.current?.()
    }
  }, [])

  return useMutation<
    MachineLocationGenerationResult | undefined,
    Error,
    {
      onEvent?: (event: WorkflowEvent) => void
      onComplete?: (result?: MachineLocationGenerationResult) => void
    }
  >({
    mutationFn: async ({ onEvent, onComplete }) => {
      pollReleaseRef.current?.()

      let aborted = false
      const abortController = new AbortController()
      const release = () => {
        aborted = true
        abortController.abort()
      }
      pollReleaseRef.current = release

      try {
        const startResponse = await apiClient.post<{ runId: string }>(
          `/machinery-directive-compliance/${complianceId}/machine-locations/generate`
        )

        const runId = startResponse.data.runId
        if (runId == null) {
          throw new Error(
            'Machine location generation did not return a run id'
          )
        }

        const startedAt = Date.now()
        let since: number | undefined = undefined

        while (true) {
          if (aborted) {
            throw new DOMException('Aborted', 'AbortError')
          }
          if (
            Date.now() - startedAt >
            MACHINE_LOCATION_GENERATION_MAX_WAIT_MS
          ) {
            throw new Error(
              'Machine location generation is taking longer than expected. Refresh the page to see if results are available.'
            )
          }

          const status = await getWorkflowStatus(runId, {
            since,
            signal: abortController.signal,
          })

          await applyWorkflowPollBatchEventsSequential(status.events, onEvent, {
            stepDelayMs: DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
            signal: abortController.signal,
          })

          if (status.status === 'completed') {
            const result = parseMachineLocationGenerationResult(status.result)
            onComplete?.(result)
            return result
          }
          if (status.status === 'failed') {
            throw new Error(
              status.error ?? 'Machine location generation failed'
            )
          }
          if (status.status === 'cancelled') {
            throw new Error('Machine location generation was cancelled')
          }

          since = status.latestSequence

          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
              } else {
                resolve()
              }
            }, MACHINE_LOCATION_GENERATION_POLL_MS)
          })
        }
      } catch (error) {
        if (isAbortOrCancelError(error)) {
          return undefined
        }
        throw error
      } finally {
        if (pollReleaseRef.current === release) {
          pollReleaseRef.current = null
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MACHINE_LOCATIONS_KEY, complianceId],
      })
    },
  })
}

function parseMachineLocationGenerationResult(
  result: unknown
): MachineLocationGenerationResult | undefined {
  if (result == null || typeof result !== 'object') {
    return undefined
  }
  const record = result as Record<string, unknown>
  return {
    createdCount:
      typeof record.createdCount === 'number' ? record.createdCount : undefined,
    skippedDuplicateCount:
      typeof record.skippedDuplicateCount === 'number'
        ? record.skippedDuplicateCount
        : undefined,
    skippedInvalidCount:
      typeof record.skippedInvalidCount === 'number'
        ? record.skippedInvalidCount
        : undefined,
  }
}

// --- Safety Functions ---

export const SAFETY_FUNCTIONS_KEY = 'safety-functions'
export const SAFETY_FUNCTION_KEY = 'safety-function'

export type SafetyFunctionSource = 'MANUAL' | 'EPLAN'

export type PlrProbabilityOfOccurrence = 'HIGH' | 'LOW'

export type EplanPageLocation = {
  pages: number[]
  sheet?: string | null
}

export type EplanSafetyFunctionArchitecture = {
  components: Array<{
    name: string
    cat?: string | null
    functiontypes?: string | null
    missiontime?: string | null
    channels: Array<{
      name: string
      channeltype?: string | null
      blocks: Array<{
        name: string
        elements: Array<{ name: string }>
      }>
    }>
  }>
}

export type EplanSafetyFunctionProposal = {
  eplanExtractedFunctionId: string
  name: string
  description: string | null
  type: 'ISD' | 'SCP' | 'PPE' | 'OI'
  dependsOnControlSystem: boolean | null
  eplanReferenceId: string | null
  eplanPageLocation: EplanPageLocation | null
  architecture: EplanSafetyFunctionArchitecture
}

export type SafetyFunctionLinkedRiskReductionMeasure = {
  id: string
  name: string
  type: RiskReductionMeasureType
  description: string | null
  linkedHazards: RiskReductionMeasureLinkedHazard[]
}

export type SafetyFunction = {
  id: string
  machineryDirectiveComplianceId: string
  name: string
  description: string | null
  source: SafetyFunctionSource
  eplanReferenceId?: string | null
  eplanPageLocation?: EplanPageLocation | null
  eplanExtractedFunctionId?: string | null
  sfid: string | null
  sftype: string | null
  triggerevent: string | null
  reaction: string | null
  safestate: string | null
  responsetime: string | null
  opmode: string | null
  sfinterfaces: string | null
  requestfrequency: string | null
  priority: string | null
  plr: string | null
  plrGraph?: string | null
  plrAssessment?: PlrAssessment13849 | null
  plrInferredAssessment?: PlrAssessment13849 | null
  plrAssessmentOverride?: PlrAssessmentOverride | null
  plrUndeterminedReasons?: SafetyFunctionPlrUndeterminedReason[]
  plrReviewStatus?: PlrReviewStatus
  plrReviewReason?: PlrReviewReason | null
  plrConfirmedAt?: string | null
  plrProbabilityOfOccurrence: PlrProbabilityOfOccurrence
  plrProbabilityOfOccurrenceJustification: string | null
  plcal: string | null
  plrdet: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string
  linkedRiskReductionMeasures?: SafetyFunctionLinkedRiskReductionMeasure[]
}

export type SistemaComponent = {
  id: string
  safetyFunctionId: string
  name: string
  cat: string | null
  functiontypes: string | null
  missiontime: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export type SistemaChannel = {
  id: string
  safetyFunctionId: string
  sistemaComponentId: string
  name: string
  channeltype: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export type SistemaArchitectureNodeKind = 'BLOCK' | 'ELEMENT'

export type SistemaArchitectureNode = {
  id: string
  safetyFunctionId: string
  sistemaChannelId: string
  parentNodeId: string | null
  name: string
  kind: SistemaArchitectureNodeKind
  sortOrder: number
  createdAt: string
  updatedAt: string
  childNodes?: SistemaArchitectureNode[]
}

export type SistemaChannelWithNodes = SistemaChannel & {
  sistemaArchitectureNodes: SistemaArchitectureNode[]
}

export type SistemaComponentWithChannels = SistemaComponent & {
  sistemaChannels: SistemaChannelWithNodes[]
}

export type SafetyFunctionWithArchitecture = SafetyFunction & {
  sistemaComponents: SistemaComponentWithChannels[]
}

export type SafetyFunctionUpdateInput = {
  name?: string
  description?: string | null
  source?: SafetyFunctionSource
  sfid?: string | null
  sftype?: string | null
  triggerevent?: string | null
  reaction?: string | null
  safestate?: string | null
  responsetime?: string | null
  opmode?: string | null
  sfinterfaces?: string | null
  requestfrequency?: string | null
  priority?: string | null
  plcal?: string | null
  plrdet?: string | null
  sortOrder?: number
  plrProbabilityOfOccurrence?: PlrProbabilityOfOccurrence
  plrProbabilityOfOccurrenceJustification?: string | null
  plrOverrideSeverity?: Severity13849 | null
  plrOverrideFrequency?: Frequency13849 | null
  plrOverridePossibilityOfAvoidance?: Possibility13849 | null
}

export type SafetyFunctionCreateInput = SafetyFunctionUpdateInput & {
  name: string
}

export type SistemaComponentInput = {
  name: string
  cat?: string | null
  functiontypes?: string | null
  missiontime?: string | null
  sortOrder?: number
}

export type SistemaChannelInput = {
  name: string
  channeltype?: string | null
  sortOrder?: number
}

export type SistemaArchitectureNodeInput = {
  name: string
  kind: SistemaArchitectureNodeKind
  parentNodeId?: string | null
  sortOrder?: number
}

function invalidateSafetyFunctionsList(
  queryClient: ReturnType<typeof useQueryClient>,
  complianceId: string
) {
  queryClient.invalidateQueries({
    queryKey: [SAFETY_FUNCTIONS_KEY, complianceId],
  })
}

function invalidateAllSafetyFunctions(
  queryClient: ReturnType<typeof useQueryClient>,
  complianceId: string
) {
  invalidateSafetyFunctionsList(queryClient, complianceId)
  queryClient.invalidateQueries({
    queryKey: [SAFETY_FUNCTION_KEY, complianceId],
  })
}

function invalidateSafetyFunctionDetail(
  queryClient: ReturnType<typeof useQueryClient>,
  complianceId: string,
  safetyFunctionId: string
) {
  queryClient.invalidateQueries({
    queryKey: [SAFETY_FUNCTION_KEY, complianceId, safetyFunctionId],
  })
}

export function useListSafetyFunctions(complianceId: string | undefined) {
  return useQuery<SafetyFunctionWithArchitecture[], Error>({
    queryKey: [SAFETY_FUNCTIONS_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<SafetyFunctionWithArchitecture[]>(
        `/machinery-directive-compliance/${complianceId}/safety-functions`
      )
      return response.data
    },
    enabled: !!complianceId,
  })
}

export function useGetSafetyFunction(
  complianceId: string | undefined,
  safetyFunctionId: string | undefined
) {
  return useQuery<SafetyFunctionWithArchitecture, Error>({
    queryKey: [SAFETY_FUNCTION_KEY, complianceId, safetyFunctionId],
    queryFn: async () => {
      if (!complianceId || !safetyFunctionId) {
        throw new Error('Compliance ID and safety function ID are required')
      }
      const response = await apiClient.get<SafetyFunctionWithArchitecture>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}`
      )
      return response.data
    },
    enabled: !!complianceId && !!safetyFunctionId,
  })
}

export function useCreateSafetyFunction(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<SafetyFunction, Error, SafetyFunctionCreateInput>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<SafetyFunction>(
        `/machinery-directive-compliance/${complianceId}/safety-functions`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAFETY_FUNCTIONS_KEY, complianceId],
      })
    },
  })
}

export function useUpdateSafetyFunction(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    SafetyFunction,
    Error,
    { safetyFunctionId: string } & SafetyFunctionUpdateInput
  >({
    mutationFn: async ({ safetyFunctionId, ...payload }) => {
      const response = await apiClient.patch<SafetyFunction>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}`,
        payload
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SAFETY_FUNCTIONS_KEY, complianceId],
      })
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        variables.safetyFunctionId
      )
      if (
        variables.plrProbabilityOfOccurrence !== undefined ||
        variables.plrProbabilityOfOccurrenceJustification !== undefined
      ) {
        queryClient.invalidateQueries({
          queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
        })
      }
    },
  })
}

export function useConfirmSafetyFunctionPlr(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<SafetyFunctionWithArchitecture, Error, string>({
    mutationFn: async (safetyFunctionId) => {
      const response = await apiClient.post<SafetyFunctionWithArchitecture>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/confirm-plr`
      )
      return response.data
    },
    onSuccess: (_data, safetyFunctionId) => {
      invalidateAllSafetyFunctions(queryClient, complianceId)
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useResetPlrAssessmentOverride(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<SafetyFunctionWithArchitecture, Error, string>({
    mutationFn: async (safetyFunctionId) => {
      const response = await apiClient.post<SafetyFunctionWithArchitecture>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/reset-plr-assessment-override`
      )
      return response.data
    },
    onSuccess: (_data, safetyFunctionId) => {
      invalidateAllSafetyFunctions(queryClient, complianceId)
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useDeleteSafetyFunction(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (safetyFunctionId) => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAFETY_FUNCTIONS_KEY, complianceId],
      })
    },
  })
}

export type SistemaImportPreviewSafetyFunction = {
  name: string
  plcal: string | null
  componentCount: number
}

export type SistemaImportPreview = {
  projectName: string | null
  existing: {
    safetyFunctionCount: number
    linkedRiskReductionMeasureCount: number
  }
  incoming: {
    safetyFunctionCount: number
    componentCount: number
    channelCount: number
    blockCount: number
    elementCount: number
    safetyFunctions: SistemaImportPreviewSafetyFunction[]
  }
}

export type SistemaImportResult = {
  deletedSafetyFunctionCount: number
  projectName: string | null
  imported: SistemaImportPreview['incoming']
}

export function usePreviewSistemaImport(complianceId: string) {
  return useMutation<SistemaImportPreview, Error, { content: string }>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<SistemaImportPreview>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/sistema-import/preview`,
        payload
      )
      return response.data
    },
  })
}

export function useImportSistemaFile(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<SistemaImportResult, Error, { content: string }>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<SistemaImportResult>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/sistema-import`,
        {
          ...payload,
          confirmReplace: true,
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAFETY_FUNCTIONS_KEY, complianceId],
      })
      queryClient.removeQueries({
        queryKey: [SAFETY_FUNCTION_KEY, complianceId],
      })
    },
  })
}

export function useCreateSistemaComponent(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<SistemaComponent, Error, SistemaComponentInput>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<SistemaComponent>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/components`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useUpdateSistemaComponent(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    SistemaComponent,
    Error,
    { componentId: string } & SistemaComponentInput
  >({
    mutationFn: async ({ componentId, ...payload }) => {
      const response = await apiClient.patch<SistemaComponent>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/components/${componentId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useDeleteSistemaComponent(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (componentId) => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/components/${componentId}`
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useCreateSistemaChannel(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    SistemaChannel,
    Error,
    { componentId: string } & SistemaChannelInput
  >({
    mutationFn: async ({ componentId, ...payload }) => {
      const response = await apiClient.post<SistemaChannel>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/components/${componentId}/channels`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useUpdateSistemaChannel(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    SistemaChannel,
    Error,
    { channelId: string } & SistemaChannelInput
  >({
    mutationFn: async ({ channelId, ...payload }) => {
      const response = await apiClient.patch<SistemaChannel>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/channels/${channelId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useDeleteSistemaChannel(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (channelId) => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/channels/${channelId}`
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useCreateSistemaArchitectureNode(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    SistemaArchitectureNode,
    Error,
    { channelId: string } & SistemaArchitectureNodeInput
  >({
    mutationFn: async ({ channelId, ...payload }) => {
      const response = await apiClient.post<SistemaArchitectureNode>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/channels/${channelId}/nodes`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useUpdateSistemaArchitectureNode(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    SistemaArchitectureNode,
    Error,
    { nodeId: string } & Partial<SistemaArchitectureNodeInput>
  >({
    mutationFn: async ({ nodeId, ...payload }) => {
      const response = await apiClient.patch<SistemaArchitectureNode>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/nodes/${nodeId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useDeleteSistemaArchitectureNode(
  complianceId: string,
  safetyFunctionId: string
) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (nodeId) => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/machinery-directive-compliance/${complianceId}/safety-functions/${safetyFunctionId}/nodes/${nodeId}`
      )
      return response.data
    },
    onSuccess: () => {
      invalidateSafetyFunctionDetail(
        queryClient,
        complianceId,
        safetyFunctionId
      )
    },
  })
}

export function useStartRrmGeneration(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    {
      selectedMeasureIds?: string[]
      onEvent?: (event: RrmGenerationStreamEvent) => void
      onComplete?: () => void
    }
  >({
    mutationFn: async ({ selectedMeasureIds, onEvent, onComplete }) => {
      const url = getStreamUrl(
        `/machinery-directive-compliance/${complianceId}/risk-reduction-measures/generate-stream`
      )
      const hasPayload = selectedMeasureIds != null
      const body = hasPayload
        ? JSON.stringify({ selectedMeasureIds })
        : undefined
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          ...(body != null && { 'Content-Type': 'application/json' }),
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        ...(body != null && { body }),
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start RRM generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(data) as RrmGenerationStreamEvent
            onEvent?.(event)
            if (
              event.type === HazardGenerationWorkflowEventType.WorkflowCompleted
            ) {
              onComplete?.()
            } else if (
              event.type === HazardGenerationWorkflowEventType.WorkflowFailed ||
              (event.type === HazardGenerationWorkflowEventType.Error &&
                !('step' in event ? event.step : undefined))
            ) {
              throw new Error(
                event.type === HazardGenerationWorkflowEventType.Error
                  ? event.error
                  : ((event as { error?: string }).error ?? 'Workflow failed')
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
    },
  })
}

export function useStartEplanSafetyFunctionExtraction(complianceId: string) {
  return useMutation<
    void,
    Error,
    {
      regenerate?: boolean
      onEvent?: (event: EplanSafetyFunctionExtractionStreamEvent) => void
      onComplete?: (result: EplanSafetyFunctionExtractionResult) => void
    }
  >({
    mutationFn: async ({ regenerate, onEvent, onComplete }) => {
      const url = getStreamUrl(
        `/machinery-directive-compliance/${complianceId}/safety-functions/extract-from-eplan/stream`
      )
      const body = JSON.stringify(
        regenerate === true ? { regenerate: true } : {}
      )
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body,
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start E-plan extraction: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(
              data
            ) as EplanSafetyFunctionExtractionStreamEvent
            onEvent?.(event)
            if (
              event.type === HazardGenerationWorkflowEventType.WorkflowCompleted
            ) {
              onComplete?.({
                ...event.result,
                proposals: event.result.proposals ?? [],
              })
            } else if (
              event.type === HazardGenerationWorkflowEventType.WorkflowFailed ||
              (event.type === HazardGenerationWorkflowEventType.Error &&
                !('step' in event ? event.step : undefined))
            ) {
              throw new Error(
                event.type === HazardGenerationWorkflowEventType.Error
                  ? event.error
                  : ((event as { error?: string }).error ?? 'Workflow failed')
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
  })
}

export function useStartEplanSafetyFunctionImport(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    {
      selectedFunctionIds: string[]
      proposals: EplanSafetyFunctionProposal[]
      onEvent?: (event: EplanSafetyFunctionImportStreamEvent) => void
      onComplete?: () => void
    }
  >({
    mutationFn: async ({
      selectedFunctionIds,
      proposals,
      onEvent,
      onComplete,
    }) => {
      const url = getStreamUrl(
        `/machinery-directive-compliance/${complianceId}/safety-functions/import-from-eplan/stream`
      )
      const body = JSON.stringify({ selectedFunctionIds, proposals })
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body,
      })
      if (!response.ok) {
        throw new Error(`Failed to start E-plan import: ${response.statusText}`)
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(
              data
            ) as EplanSafetyFunctionImportStreamEvent
            onEvent?.(event)
            if (
              event.type === HazardGenerationWorkflowEventType.WorkflowCompleted
            ) {
              onComplete?.()
            } else if (
              event.type === HazardGenerationWorkflowEventType.WorkflowFailed ||
              (event.type === HazardGenerationWorkflowEventType.Error &&
                !('step' in event ? event.step : undefined))
            ) {
              throw new Error(
                event.type === HazardGenerationWorkflowEventType.Error
                  ? event.error
                  : ((event as { error?: string }).error ?? 'Workflow failed')
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAFETY_FUNCTIONS_KEY, complianceId],
      })
    },
  })
}

// --- Risk Assessment Steps (per hazard) ---

export type RiskAssessmentStep = {
  id: string
  hazardIdentificationId: string
  riskReductionMeasureId: string
  riskReductionMeasure: RiskReductionMeasure
  sequence: number
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  riskInInjury: string | null
  riskInFrequency: string | null
  riskInAvoidance: string | null
  riskInLikelihood: string | null
  riskInInjuryJustification: string | null
  riskInFrequencyJustification: string | null
  riskInAvoidanceJustification: string | null
  riskInLikelihoodJustification: string | null
  riskOutInjury: string | null
  riskOutFrequency: string | null
  riskOutAvoidance: string | null
  riskOutLikelihood: string | null
  riskOutInjuryJustification: string | null
  riskOutFrequencyJustification: string | null
  riskOutAvoidanceJustification: string | null
  riskOutLikelihoodJustification: string | null
  riskOutScore: number | null
  confirmationPending?: boolean
}

export type HazardIdentificationWithRrmSteps = HazardIdentification & {
  hazardRiskReductionSteps: RiskAssessmentStep[]
}

/** Derived: hazard risk-in = first step's risk-in (or null if no steps). */
export function getHazardRiskIn(
  steps: RiskAssessmentStep[]
): RiskAssessmentStep['riskInInjury'] {
  const first = steps[0]
  return first?.riskInInjury ?? null
}

/** Derived: hazard risk-out = last step's risk-out (or null if no steps). */
export function getHazardRiskOut(
  steps: RiskAssessmentStep[]
): RiskAssessmentStep['riskOutInjury'] {
  if (steps.length === 0) {
    return null
  }
  return steps[steps.length - 1].riskOutInjury ?? null
}

export const HAZARD_RISK_REDUCTION_STEPS_KEY = 'hazard-risk-reduction-steps'

export async function fetchHazardRiskReductionSteps(
  hazardId: string
): Promise<RiskAssessmentStep[]> {
  const response = await apiClient.get<RiskAssessmentStep[]>(
    `/hazards/${hazardId}/hazard-risk-reduction-steps`
  )
  return response.data
}

export function useListHazardRiskReductionSteps(hazardId: string | undefined) {
  return useQuery<RiskAssessmentStep[], Error>({
    queryKey: [HAZARD_RISK_REDUCTION_STEPS_KEY, hazardId],
    queryFn: () => fetchHazardRiskReductionSteps(hazardId!),
    enabled: !!hazardId,
  })
}

export type HazardRiskReductionStepInput = {
  riskReductionMeasureId: string
  sequence?: number
  riskInInjury?: string | null
  riskInFrequency?: string | null
  riskInAvoidance?: string | null
  riskInLikelihood?: string | null
  riskInInjuryJustification?: string | null
  riskInFrequencyJustification?: string | null
  riskInAvoidanceJustification?: string | null
  riskInLikelihoodJustification?: string | null
  riskOutInjury?: string | null
  riskOutFrequency?: string | null
  riskOutAvoidance?: string | null
  riskOutLikelihood?: string | null
  riskOutInjuryJustification?: string | null
  riskOutFrequencyJustification?: string | null
  riskOutAvoidanceJustification?: string | null
  riskOutLikelihoodJustification?: string | null
  riskOutScore?: number | null
}

export function useCreateHazardRiskReductionStep(
  hazardId: string,
  complianceId?: string
) {
  const queryClient = useQueryClient()
  return useMutation<RiskAssessmentStep, Error, HazardRiskReductionStepInput>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<RiskAssessmentStep>(
        `/hazards/${hazardId}/hazard-risk-reduction-steps`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_RISK_REDUCTION_STEPS_KEY, hazardId],
      })
      if (complianceId) {
        queryClient.invalidateQueries({
          queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
        })
        invalidateAllSafetyFunctions(queryClient, complianceId)
      }
    },
  })
}

export function useUpdateHazardRiskReductionStep(
  hazardId: string,
  complianceId?: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    RiskAssessmentStep,
    Error,
    { stepId: string } & Partial<HazardRiskReductionStepInput>
  >({
    mutationFn: async ({ stepId, ...payload }) => {
      const response = await apiClient.patch<RiskAssessmentStep>(
        `/hazards/${hazardId}/hazard-risk-reduction-steps/${stepId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_RISK_REDUCTION_STEPS_KEY, hazardId],
      })
      if (complianceId) {
        queryClient.invalidateQueries({
          queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
        })
        invalidateAllSafetyFunctions(queryClient, complianceId)
      }
    },
  })
}

export function useDeleteAllHazardRiskReductionStepsForHazard(
  hazardId: string,
  complianceId?: string
) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiClient.delete(`/hazards/${hazardId}/hazard-risk-reduction-steps`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_RISK_REDUCTION_STEPS_KEY, hazardId],
      })
      if (complianceId) {
        queryClient.invalidateQueries({
          queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
        })
        invalidateAllSafetyFunctions(queryClient, complianceId)
      }
    },
  })
}

export function useDeleteHazardRiskReductionStepsForMeasure(
  complianceId: string,
  riskReductionMeasureId: string
) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { hazardId: string }>({
    mutationFn: async ({ hazardId }) => {
      await apiClient.delete(
        `/hazards/${hazardId}/hazard-risk-reduction-steps`,
        {
          params: { riskReductionMeasureId },
        }
      )
    },
    onSuccess: (_data, { hazardId }) => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_RISK_REDUCTION_STEPS_KEY, hazardId],
      })
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [RISK_REDUCTION_MEASURES_KEY, complianceId],
      })
    },
  })
}

export type RiskOutGenerationMode = 'dimensions' | 'justifications' | 'both'

export type RiskOutFitStatus = 'applicable' | 'incompatible'

export type RiskOutFitAssessmentFields = {
  fitStatus: RiskOutFitStatus
  fitSummary: string
}

export type RiskOutFitAssessment = {
  fitStatus: 'incompatible'
  fitSummary: string
}

export type RiskOutDimensionsResult = RiskOutFitAssessmentFields & {
  riskOutInjury: string
  riskOutFrequency: string
  riskOutAvoidance: string
  riskOutLikelihood: string
}

export type RiskOutJustificationsResult = RiskOutFitAssessmentFields & {
  riskOutInjuryJustification: string
  riskOutFrequencyJustification: string
  riskOutAvoidanceJustification: string
  riskOutLikelihoodJustification: string
}

export type RiskOutGenerationResult = RiskOutDimensionsResult &
  RiskOutJustificationsResult

export type GenerateRiskOutResult =
  | RiskOutDimensionsResult
  | RiskOutJustificationsResult
  | RiskOutGenerationResult

export type GenerateRiskOutVariables =
  | {
      mode: 'dimensions' | 'both'
      riskReductionMeasureId: string
      excludeStepId?: string
    }
  | {
      mode: 'justifications'
      riskReductionMeasureId: string
      excludeStepId?: string
      riskOutInjury: string
      riskOutFrequency: string
      riskOutAvoidance: string
      riskOutLikelihood: string
    }

export function useGenerateRiskOut(hazardId: string) {
  return useMutation<GenerateRiskOutResult, Error, GenerateRiskOutVariables>({
    mutationFn: async (variables) => {
      const url = getStreamUrl(`/hazards/${hazardId}/generate-risk-out-stream`)
      const body = JSON.stringify(variables)
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body,
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start risk out generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let result: GenerateRiskOutResult | null = null
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(data) as WorkflowEvent
            if (event.type === WorkflowEventType.WorkflowCompleted) {
              result = event.result as GenerateRiskOutResult
            } else if (
              event.type === WorkflowEventType.WorkflowFailed ||
              (event.type === WorkflowEventType.Error &&
                !('step' in event ? event.step : undefined))
            ) {
              throw new Error(
                event.type === WorkflowEventType.Error
                  ? event.error
                  : ((event as { error?: string }).error ?? 'Workflow failed')
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
      if (!result) {
        throw new Error('Risk out generation completed without result')
      }
      return result
    },
  })
}

export type ApplicableRiskReductionMeasureRow = {
  riskReductionMeasureId: string
  reason: string
}

export type SuggestApplicableRiskReductionMeasuresResult = {
  measures: ApplicableRiskReductionMeasureRow[]
  refreshedAt: string
}

export type SuggestApplicableRiskReductionMeasuresVariables = {
  hazardId: string
  language: string
}

export function useSuggestApplicableRiskReductionMeasures() {
  return useMutation<
    SuggestApplicableRiskReductionMeasuresResult,
    Error,
    SuggestApplicableRiskReductionMeasuresVariables
  >({
    mutationFn: async ({ hazardId, language }) => {
      const response =
        await apiClient.post<SuggestApplicableRiskReductionMeasuresResult>(
          `/hazards/${hazardId}/applicable-risk-reduction-measures`,
          { language }
        )
      return response.data
    },
  })
}

export const HazardGenerationWorkflowEventType = {
  WorkflowCompleted: 'workflow.completed',
  WorkflowFailed: 'workflow.failed',
  Error: 'error',
} as const

export function useStartHazardGeneration(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    {
      selectedHazardIds?: string[]
      onEvent?: (event: HazardGenerationStreamEvent) => void
      onComplete?: () => void
    }
  >({
    mutationFn: async ({ selectedHazardIds, onEvent, onComplete }) => {
      const url = getStreamUrl(
        `/machinery-directive-compliance/${complianceId}/hazards/generate-stream`
      )
      const hasPayload = selectedHazardIds != null
      const body = hasPayload
        ? JSON.stringify({
            ...(selectedHazardIds != null && { selectedHazardIds }),
          })
        : undefined
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          ...(body != null && { 'Content-Type': 'application/json' }),
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        ...(body != null && { body }),
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start hazard generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(data) as HazardGenerationStreamEvent
            onEvent?.(event)
            if (
              event.type === HazardGenerationWorkflowEventType.WorkflowCompleted
            ) {
              onComplete?.()
            } else if (
              event.type === HazardGenerationWorkflowEventType.WorkflowFailed ||
              (event.type === HazardGenerationWorkflowEventType.Error &&
                !('step' in event ? event.step : undefined))
            ) {
              throw new Error(
                event.type === HazardGenerationWorkflowEventType.Error
                  ? event.error
                  : ((event as { error?: string }).error ?? 'Workflow failed')
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HAZARD_IDENTIFICATIONS_KEY, complianceId],
      })
    },
  })
}

export function useGetRiskAssessmentDocument(complianceId: string | undefined) {
  return useQuery<RiskAssessmentDocument | null, Error>({
    queryKey: [RISK_ASSESSMENT_DOCUMENT_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      try {
        const response = await apiClient.get<RiskAssessmentDocument>(
          `/machinery-directive-compliance/${complianceId}/risk-assessment`
        )
        return response.data
      } catch (error: unknown) {
        const status =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined
        if (status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!complianceId,
  })
}

export function useUpdateRiskAssessmentDocument(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    RiskAssessmentDocument,
    Error,
    { sections: RiskAssessmentSectionRecord[]; skipCacheInvalidation?: boolean }
  >({
    mutationFn: async ({ sections }) => {
      const response = await apiClient.patch<RiskAssessmentDocument>(
        `/machinery-directive-compliance/${complianceId}/risk-assessment`,
        { sections }
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      if (variables.skipCacheInvalidation) {
        return
      }
      queryClient.invalidateQueries({
        queryKey: [RISK_ASSESSMENT_DOCUMENT_KEY, complianceId],
      })
    },
  })
}

export function useCreateRiskAssessmentDocumentVersion(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    RiskAssessmentDocument,
    Error,
    { sections: RiskAssessmentSectionRecord[] }
  >({
    mutationFn: async ({ sections }) => {
      const response = await apiClient.post<RiskAssessmentDocument>(
        `/machinery-directive-compliance/${complianceId}/risk-assessment/versions`,
        { sections }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_ASSESSMENT_DOCUMENT_KEY, complianceId],
      })
    },
  })
}

export type RewriteInstructionResponse = {
  result: string
  runId: string
}

export type RewriteInstructionPayload = {
  complianceId: string
  documentType: ComplianceDocumentRewriteDocumentType
  sectionId: string
  sectionKind: ComplianceDocumentSectionKind
  sectionMarkdown: string
  originalText: string
  instruction: string
}

export {
  REWRITE_GATE_UNAVAILABLE_ERROR_CODE,
  REWRITE_INSUFFICIENT_SOURCES_ERROR_CODE,
  REWRITE_INSTRUCTION_REFUSED_ERROR_CODE,
  REWRITE_INVALID_OUTPUT_ERROR_CODE,
}

export type {
  RewriteInstructionHandledError,
  RewriteInstructionMutationResult,
  RewriteInstructionSuccess,
} from './rewriteInstructionStream'

export function useRewriteInstruction() {
  return useMutation<
    RewriteInstructionMutationResult,
    Error,
    RewriteInstructionPayload
  >({
    mutationFn: async (payload) => {
      const url = getStreamUrl('/machinery-directive-compliance/rewrite-stream')
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error(`Failed to start rewrite: ${response.statusText}`)
      }
      if (response.body == null) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let result: RewriteInstructionMutationResult | null = null
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const parsed = parseRewriteSseBuffer(buffer, result)
          buffer = parsed.remaining
          result = parsed.result
        }
        if (buffer.trim().length > 0) {
          const parsed = parseRewriteSseBuffer(`${buffer}\n`, result)
          result = parsed.result
        }
      } finally {
        reader.releaseLock()
      }
      if (result == null) {
        throw new Error('Rewrite completed without result')
      }
      return result
    },
  })
}

export function useRecordComplianceDocumentRewriteOutcome() {
  return useMutation<
    { ok: boolean },
    Error,
    { runId: string; outcome: ComplianceDocumentRewriteOutcome }
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.post<{ ok: boolean }>(
        '/machinery-directive-compliance/rewrite-outcome',
        payload
      )
      return response.data
    },
  })
}

export type ComplianceImageUploadUrlResponse = { url: string }

export async function getComplianceImageUploadUrl(
  complianceId: string,
  key: string
): Promise<ComplianceImageUploadUrlResponse> {
  const response = await apiClient.post<ComplianceImageUploadUrlResponse>(
    `/machinery-directive-compliance/${complianceId}/image-upload-url`,
    { key }
  )
  return response.data
}

export function getComplianceImageProxyUrl(
  complianceId: string,
  path: string
): string {
  const baseURL = apiClient.defaults.baseURL ?? ''
  return `${baseURL}/machinery-directive-compliance/${complianceId}/images/${path}`
}

function getStreamUrl(path: string): string {
  const baseURL = apiClient.defaults.baseURL ?? ''
  return `${baseURL}${path}`
}

function getStreamHeaders(): Record<string, string> {
  const common = (apiClient.defaults.headers.common ?? {}) as Record<
    string,
    string
  >
  const { 'Content-Type': _ct, ...rest } = common
  return rest
}

export function useStartHighRiskCheck(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    { onEvent?: (event: WorkflowEvent) => void; onComplete?: () => void }
  >({
    mutationFn: async ({ onEvent, onComplete }) => {
      const url = getStreamUrl(
        `/machinery-directive-compliance/${complianceId}/high-risk-check/stream`
      )
      const response = await fetch(url, {
        method: 'POST',
        headers: getStreamHeaders(),
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start high risk check: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(data) as WorkflowEvent
            onEvent?.(event)
            if (event.type === WorkflowEventType.WorkflowCompleted) {
              onComplete?.()
            } else if (
              event.type === WorkflowEventType.WorkflowFailed ||
              (event.type === WorkflowEventType.Error &&
                !('step' in event ? event.step : undefined))
            ) {
              throw new Error(
                event.type === WorkflowEventType.Error
                  ? event.error
                  : ((event as { error?: string }).error ?? 'Workflow failed')
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

export function useStartStandardApplicabilityDiscovery(complianceId: string) {
  const queryClient = useQueryClient()
  const pollReleaseRef = useRef<(() => void) | null>(null)
  const runIdRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      pollReleaseRef.current?.()
    }
  }, [])

  const mutation = useMutation<
    void,
    Error,
    { onEvent?: (event: WorkflowEvent) => void; onComplete?: () => void }
  >({
    mutationFn: async ({ onEvent, onComplete }) => {
      pollReleaseRef.current?.()
      runIdRef.current = null

      let aborted = false
      const abortController = new AbortController()
      const release = () => {
        aborted = true
        abortController.abort()
      }
      pollReleaseRef.current = release

      try {
        const startResponse = await apiClient.post<{ runId: string }>(
          `/machinery-directive-compliance/${complianceId}/standard-applicability/discover`
        )
        const { runId } = startResponse.data
        runIdRef.current = runId

        const startedAt = Date.now()
        let since: number | undefined = undefined

        while (true) {
          if (aborted) {
            throw new DOMException('Aborted', 'AbortError')
          }
          if (
            Date.now() - startedAt >
            STANDARD_APPLICABILITY_DISCOVERY_MAX_WAIT_MS
          ) {
            throw new Error(
              'Standard applicability discovery is taking longer than expected. Refresh the page to see if results are available.'
            )
          }

          const status = await getWorkflowStatus(runId, {
            since,
            signal: abortController.signal,
          })

          await applyWorkflowPollBatchEventsSequential(status.events, onEvent, {
            stepDelayMs: DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
            signal: abortController.signal,
          })

          if (status.status === 'completed') {
            onComplete?.()
            return
          }
          if (status.status === 'failed') {
            throw new Error(
              status.error ?? 'Standard applicability discovery failed'
            )
          }
          if (status.status === 'cancelled') {
            throw new Error('Standard applicability discovery was cancelled')
          }

          since = status.latestSequence

          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
              } else {
                resolve()
              }
            }, STANDARD_APPLICABILITY_DISCOVERY_POLL_MS)
          })
        }
      } catch (error) {
        if (isAbortOrCancelError(error)) {
          return
        }
        throw error
      } finally {
        runIdRef.current = null
        if (pollReleaseRef.current === release) {
          pollReleaseRef.current = null
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
      })
    },
  })

  const cancel = useCallback(async () => {
    const runId = runIdRef.current
    if (runId != null) {
      await cancelWorkflow(runId)
    }
  }, [])

  return { ...mutation, cancel }
}

export function useStartTypeCStandardApplicabilityCheck(complianceId: string) {
  const queryClient = useQueryClient()
  const pollReleaseRef = useRef<(() => void) | null>(null)
  const runIdRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      pollReleaseRef.current?.()
    }
  }, [])

  const mutation = useMutation<
    void,
    Error,
    { onEvent?: (event: WorkflowEvent) => void; onComplete?: () => void }
  >({
    mutationFn: async ({ onEvent, onComplete }) => {
      pollReleaseRef.current?.()
      runIdRef.current = null

      let aborted = false
      const abortController = new AbortController()
      const release = () => {
        aborted = true
        abortController.abort()
      }
      pollReleaseRef.current = release

      try {
        const startResponse = await apiClient.post<{ runId: string }>(
          `/machinery-directive-compliance/${complianceId}/standard-applicability/discover-type-c`
        )
        const { runId } = startResponse.data
        runIdRef.current = runId

        const startedAt = Date.now()
        let since: number | undefined = undefined

        while (true) {
          if (aborted) {
            throw new DOMException('Aborted', 'AbortError')
          }
          if (
            Date.now() - startedAt >
            STANDARD_APPLICABILITY_DISCOVERY_MAX_WAIT_MS
          ) {
            throw new Error(
              'Type C standard applicability check is taking longer than expected. Refresh the page to see if results are available.'
            )
          }

          const status = await getWorkflowStatus(runId, {
            since,
            signal: abortController.signal,
          })

          await applyWorkflowPollBatchEventsSequential(status.events, onEvent, {
            stepDelayMs: DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
            signal: abortController.signal,
          })

          if (status.status === 'completed') {
            onComplete?.()
            return
          }
          if (status.status === 'failed') {
            throw new Error(
              status.error ?? 'Type C standard applicability check failed'
            )
          }
          if (status.status === 'cancelled') {
            throw new Error('Type C standard applicability check was cancelled')
          }

          since = status.latestSequence

          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
              } else {
                resolve()
              }
            }, STANDARD_APPLICABILITY_DISCOVERY_POLL_MS)
          })
        }
      } catch (error) {
        if (isAbortOrCancelError(error)) {
          return
        }
        throw error
      } finally {
        runIdRef.current = null
        if (pollReleaseRef.current === release) {
          pollReleaseRef.current = null
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
      })
    },
  })

  const cancel = useCallback(async () => {
    const runId = runIdRef.current
    if (runId != null) {
      await cancelWorkflow(runId)
    }
  }, [])

  return { ...mutation, cancel }
}

export type RerunApplicabilityCandidate = {
  reference: string
  title: string
  type: string
  applicability: string
  certainty: string
  explanation: string
}

export type MachineCharacteristicsDryRunResult = {
  dryRun: true
  extracted: true
  answers: Record<string, unknown>
  evidence: Record<string, unknown>
  templateVersion: string
  mappingVersion: string
  applicableQuestionKeys: string[]
}

export type StandardApplicabilityRerunCommitPayload = {
  typeC?:
    | { cleanupOnly: true }
    | {
        cleanupOnly?: false
        candidates: RerunApplicabilityCandidate[]
        selectedReferences: string[]
      }
  typeB?:
    | { cleanupOnly: true }
    | {
        cleanupOnly?: false
        answersJson: Record<string, unknown>
        evidenceJson: Record<string, unknown>
        templateVersion: string
        mappingVersion: string
        applicableQuestionKeys: string[]
        generationFingerprint: string
        candidates: RerunApplicabilityCandidate[]
        selectedReferences: string[]
      }
}

export function useStartTypeCStandardApplicabilityCheckDryRun(
  complianceId: string
) {
  const pollReleaseRef = useRef<(() => void) | null>(null)
  const runIdRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      pollReleaseRef.current?.()
    }
  }, [])

  const mutation = useMutation<
    RerunApplicabilityCandidate[],
    Error,
    { onEvent?: (event: WorkflowEvent) => void }
  >({
    mutationFn: async ({ onEvent }) => {
      pollReleaseRef.current?.()
      runIdRef.current = null

      let aborted = false
      const abortController = new AbortController()
      const release = () => {
        aborted = true
        abortController.abort()
      }
      pollReleaseRef.current = release

      try {
        const startResponse = await apiClient.post<{ runId: string }>(
          `/machinery-directive-compliance/${complianceId}/standard-applicability/discover-type-c?persist=false`
        )
        const { runId } = startResponse.data
        runIdRef.current = runId

        const startedAt = Date.now()
        let since: number | undefined = undefined

        while (true) {
          if (aborted) {
            throw new DOMException('Aborted', 'AbortError')
          }
          if (
            Date.now() - startedAt >
            STANDARD_APPLICABILITY_DISCOVERY_MAX_WAIT_MS
          ) {
            throw new Error(
              'Type C standard applicability check is taking longer than expected.'
            )
          }

          const status = await getWorkflowStatus(runId, {
            since,
            signal: abortController.signal,
          })

          await applyWorkflowPollBatchEventsSequential(status.events, onEvent, {
            stepDelayMs: DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
            signal: abortController.signal,
          })

          if (status.status === 'completed') {
            const result = status.result as
              | { candidates?: RerunApplicabilityCandidate[] }
              | undefined
            return result?.candidates ?? []
          }
          if (status.status === 'failed') {
            throw new Error(
              status.error ?? 'Type C standard applicability check failed'
            )
          }
          if (status.status === 'cancelled') {
            throw new Error('Type C standard applicability check was cancelled')
          }

          since = status.latestSequence

          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
              } else {
                resolve()
              }
            }, STANDARD_APPLICABILITY_DISCOVERY_POLL_MS)
          })
        }
      } catch (error) {
        if (isAbortOrCancelError(error)) {
          return []
        }
        throw error
      } finally {
        runIdRef.current = null
        if (pollReleaseRef.current === release) {
          pollReleaseRef.current = null
        }
      }
    },
  })

  const cancel = useCallback(async () => {
    pollReleaseRef.current?.()
    const runId = runIdRef.current
    if (runId != null) {
      await cancelWorkflow(runId)
    }
  }, [])

  return { ...mutation, cancel }
}

export function useStartMachineCharacteristicsExtractionDryRun(
  complianceId: string
) {
  const pollReleaseRef = useRef<(() => void) | null>(null)
  const runIdRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      pollReleaseRef.current?.()
    }
  }, [])

  const mutation = useMutation<
    MachineCharacteristicsDryRunResult | { skipped: true; reason: string },
    Error,
    { onEvent?: (event: WorkflowEvent) => void }
  >({
    mutationFn: async ({ onEvent }) => {
      pollReleaseRef.current?.()
      runIdRef.current = null

      let aborted = false
      const abortController = new AbortController()
      const release = () => {
        aborted = true
        abortController.abort()
      }
      pollReleaseRef.current = release

      try {
        const startResponse = await apiClient.post<{ runId: string | null }>(
          `/machinery-directive-compliance/${complianceId}/machine-characteristics/extract`,
          { force: true, dryRun: true }
        )
        const runId = startResponse.data.runId
        if (runId == null) {
          throw new Error(
            'Machine characteristics extraction did not return a run id'
          )
        }
        runIdRef.current = runId

        const startedAt = Date.now()
        let since: number | undefined = undefined

        while (true) {
          if (aborted) {
            throw new DOMException('Aborted', 'AbortError')
          }
          if (
            Date.now() - startedAt >
            MACHINE_CHARACTERISTICS_EXTRACTION_MAX_WAIT_MS
          ) {
            throw new Error(
              'Machine characteristics extraction is taking longer than expected.'
            )
          }

          const status = await getWorkflowStatus(runId, {
            since,
            signal: abortController.signal,
          })

          await applyWorkflowPollBatchEventsSequential(status.events, onEvent, {
            stepDelayMs: DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
            signal: abortController.signal,
          })

          if (status.status === 'completed') {
            const result = status.result as
              | MachineCharacteristicsDryRunResult
              | { skipped: true; reason: string }
              | undefined
            if (
              result != null &&
              'skipped' in result &&
              result.skipped === true
            ) {
              return result
            }
            if (
              result != null &&
              'extracted' in result &&
              result.dryRun === true &&
              result.extracted === true
            ) {
              return result
            }
            throw new Error('Unexpected machine characteristics extraction result')
          }
          if (status.status === 'failed') {
            throw new Error(
              status.error ?? 'Machine characteristics extraction failed'
            )
          }
          if (status.status === 'cancelled') {
            return { skipped: true, reason: 'cancelled' }
          }

          since = status.latestSequence

          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
              } else {
                resolve()
              }
            }, MACHINE_CHARACTERISTICS_EXTRACTION_POLL_MS)
          })
        }
      } catch (error) {
        if (isAbortOrCancelError(error)) {
          return { skipped: true, reason: 'cancelled' }
        }
        throw error
      } finally {
        runIdRef.current = null
        if (pollReleaseRef.current === release) {
          pollReleaseRef.current = null
        }
      }
    },
  })

  const cancel = useCallback(async () => {
    pollReleaseRef.current?.()
    const runId = runIdRef.current
    if (runId != null) {
      await cancelWorkflow(runId)
    }
  }, [])

  return { ...mutation, cancel }
}

export function usePreviewTypeBQuestionnaireCandidates(complianceId: string) {
  return useMutation<
    {
      candidates: RerunApplicabilityCandidate[]
      generationFingerprint: string
      completion: {
        templateVersion: string
        mappingVersion: string
        visibleQuestionKeys: string[]
      }
    },
    Error,
    { answers: Record<string, unknown> }
  >({
    mutationFn: async ({ answers }) => {
      const response = await apiClient.post<{
        candidates: RerunApplicabilityCandidate[]
        generationFingerprint: string
        completion: {
          templateVersion: string
          mappingVersion: string
          visibleQuestionKeys: string[]
        }
      }>(
        `/machinery-directive-compliance/${complianceId}/type-b-questionnaire/generate-preview`,
        { answers }
      )
      return response.data
    },
  })
}

export function useCommitStandardApplicabilityRerun(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    unknown,
    Error,
    StandardApplicabilityRerunCommitPayload
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.post(
        `/machinery-directive-compliance/${complianceId}/standard-applicability/rerun/commit`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STANDARD_APPLICABILITY_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

/**
 * Latest persisted machine characteristics extraction workflow for polling step events.
 * Poll while mounted until the run completes, fails, or none exists.
 */
export function useMachineCharacteristicsExtractionWorkflowLatest(
  complianceId: string | undefined,
  enabled = true
) {
  return useQuery<LatestWorkflowForEntityResponse, Error>({
    queryKey: [
      MACHINERY_DIRECTIVE_COMPLIANCE_KEY,
      complianceId,
      'machine-characteristics-extraction-workflow-latest',
    ],
    queryFn: async () =>
      getLatestWorkflowForEntity(
        'machinery_directive_compliance',
        complianceId!,
        'machine_characteristics_extraction'
      ),
    enabled: Boolean(complianceId) && enabled,
    refetchInterval(query) {
      const status = query.state.data?.status
      if (
        status === 'failed' ||
        status === 'completed' ||
        status === 'cancelled' ||
        status === 'none'
      ) {
        return false
      }
      return MACHINE_CHARACTERISTICS_EXTRACTION_POLL_MS
    },
    staleTime: 0,
  })
}

export function useStartMachineCharacteristicsExtraction(complianceId: string) {
  const queryClient = useQueryClient()
  const pollReleaseRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      pollReleaseRef.current?.()
    }
  }, [])

  return useMutation<
    void,
    Error,
    { onEvent?: (event: WorkflowEvent) => void; onComplete?: () => void }
  >({
    mutationFn: async ({ onEvent, onComplete }) => {
      pollReleaseRef.current?.()

      let aborted = false
      const abortController = new AbortController()
      const release = () => {
        aborted = true
        abortController.abort()
      }
      pollReleaseRef.current = release

      try {
        const startResponse = await apiClient.post<{
          runId: string | null
          alreadyCompleted?: boolean
        }>(
          `/machinery-directive-compliance/${complianceId}/machine-characteristics/extract`
        )

        if (startResponse.data.alreadyCompleted === true) {
          onComplete?.()
          return
        }

        const runId = startResponse.data.runId
        if (runId == null) {
          throw new Error(
            'Machine characteristics extraction did not return a run id'
          )
        }

        const startedAt = Date.now()
        let since: number | undefined = undefined

        while (true) {
          if (aborted) {
            throw new DOMException('Aborted', 'AbortError')
          }
          if (
            Date.now() - startedAt >
            MACHINE_CHARACTERISTICS_EXTRACTION_MAX_WAIT_MS
          ) {
            throw new Error(
              'Machine characteristics extraction is taking longer than expected. Refresh the page to see if results are available.'
            )
          }

          const status = await getWorkflowStatus(runId, {
            since,
            signal: abortController.signal,
          })

          await applyWorkflowPollBatchEventsSequential(status.events, onEvent, {
            stepDelayMs: DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS,
            signal: abortController.signal,
          })

          if (status.status === 'completed') {
            onComplete?.()
            return
          }
          if (status.status === 'failed') {
            throw new Error(
              status.error ?? 'Machine characteristics extraction failed'
            )
          }
          if (status.status === 'cancelled') {
            throw new Error('Machine characteristics extraction was cancelled')
          }

          since = status.latestSequence

          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
              } else {
                resolve()
              }
            }, MACHINE_CHARACTERISTICS_EXTRACTION_POLL_MS)
          })
        }
      } catch (error) {
        if (isAbortOrCancelError(error)) {
          return
        }
        throw error
      } finally {
        if (pollReleaseRef.current === release) {
          pollReleaseRef.current = null
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
      queryClient.invalidateQueries({
        queryKey: [TYPE_B_QUESTIONNAIRE_KEY, complianceId],
      })
    },
  })
}

export function useStartRiskAssessmentGeneration(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    {
      onEvent?: (event: RiskAssessmentStreamEvent) => void
      onComplete?: () => void
      /** Required for full-document generation; omit for partial section regen. */
      language?: string
      /** When non-empty, server regenerates only these sections and merges into the latest document. */
      sectionsToGenerate?: string[]
    }
  >({
    mutationFn: async ({
      onEvent,
      onComplete,
      language,
      sectionsToGenerate,
    }) => {
      const hasSections =
        sectionsToGenerate != null && sectionsToGenerate.length > 0
      const query =
        language != null && language.length > 0
          ? `?language=${encodeURIComponent(language)}`
          : ''
      const url = `${getStreamUrl(
        `/machinery-directive-compliance/${complianceId}/risk-assessment-stream`
      )}${query}`
      const body = hasSections
        ? JSON.stringify({ sectionsToGenerate })
        : undefined
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          Accept: 'text/event-stream',
          ...(body != null ? { 'Content-Type': 'application/json' } : {}),
        },
        credentials: 'include',
        body,
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start risk assessment generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            type StreamPayload =
              | RiskAssessmentStreamEvent
              | { type: 'workflow.failed'; error?: string }
            const event = JSON.parse(data) as StreamPayload
            if (
              event.type?.startsWith('document_section.') ||
              event.type === 'document.completed' ||
              event.type === 'error'
            ) {
              onEvent?.(event as RiskAssessmentStreamEvent)
            }
            if (event.type === 'document.completed') {
              onComplete?.()
              return
            }
            if (event.type === 'error') {
              throw new Error(
                (event as RiskAssessmentStreamEvent & { error?: string })
                  .error ?? 'Generation failed'
              )
            }
            if (event.type === 'workflow.failed') {
              throw new Error(event.error ?? 'Workflow failed')
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RISK_ASSESSMENT_DOCUMENT_KEY, complianceId],
      })
    },
  })
}

async function downloadExport(
  url: string,
  filename: string,
  notFoundMessage: string
): Promise<void> {
  const headers = {
    ...apiClient.defaults.headers.common,
  } as Record<string, string>
  delete headers['Content-Type']
  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  })
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(notFoundMessage)
    }
    throw new Error(`Export failed: ${response.statusText}`)
  }
  const blob = await response.blob()
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  link.click()
  URL.revokeObjectURL(downloadUrl)
}

export type ExportCompliancePdfPayload = {
  coverMarkdown: string
  /** Localized title for Content-Disposition / download name */
  documentName?: string
}

function defaultPdfDownloadFilename(
  documentName: string | undefined,
  fallback: string
): string {
  if (documentName == null || documentName.trim() === '') {
    return fallback
  }
  const trimmed = documentName.trim()
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`
}

async function downloadExportPost(
  url: string,
  payload: ExportCompliancePdfPayload,
  fallbackFilename: string,
  notFoundMessage: string
): Promise<void> {
  const headers = {
    ...apiClient.defaults.headers.common,
    'Content-Type': 'application/json',
  } as Record<string, string>
  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      coverMarkdown: payload.coverMarkdown,
      documentName: payload.documentName,
    }),
  })
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(notFoundMessage)
    }
    throw new Error(`Export failed: ${response.statusText}`)
  }
  const blob = await response.blob()
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = defaultPdfDownloadFilename(
    payload.documentName,
    fallbackFilename
  )
  link.click()
  URL.revokeObjectURL(downloadUrl)
}

export function useExportRiskAssessmentPdf(complianceId: string) {
  return useMutation<void, Error, ExportCompliancePdfPayload>({
    mutationFn: async (payload) => {
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/machinery-directive-compliance/${complianceId}/risk-assessment/export-pdf`
      const exportFilename = buildRiskAssessmentPdfFilename(new Date())
      await downloadExportPost(
        url,
        payload,
        exportFilename,
        'No risk assessment document found to export'
      )
    },
  })
}

export function useGetInstructionHandbookDocument(
  complianceId: string | undefined
) {
  return useQuery<InstructionHandbookDocument | null, Error>({
    queryKey: [INSTRUCTION_HANDBOOK_DOCUMENT_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      try {
        const response = await apiClient.get<InstructionHandbookDocument>(
          `/machinery-directive-compliance/${complianceId}/instruction-handbook`
        )
        return response.data
      } catch (error: unknown) {
        const status =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined
        if (status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!complianceId,
  })
}

export function useUpdateInstructionHandbookDocument(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    InstructionHandbookDocument,
    Error,
    {
      sections: InstructionHandbookSectionRecord[]
      skipCacheInvalidation?: boolean
    }
  >({
    mutationFn: async ({ sections }) => {
      const response = await apiClient.patch<InstructionHandbookDocument>(
        `/machinery-directive-compliance/${complianceId}/instruction-handbook`,
        { sections }
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      if (variables.skipCacheInvalidation) {
        return
      }
      queryClient.invalidateQueries({
        queryKey: [INSTRUCTION_HANDBOOK_DOCUMENT_KEY, complianceId],
      })
    },
  })
}

export function useCreateInstructionHandbookDocumentVersion(
  complianceId: string
) {
  const queryClient = useQueryClient()
  return useMutation<
    InstructionHandbookDocument,
    Error,
    { sections: InstructionHandbookSectionRecord[] }
  >({
    mutationFn: async ({ sections }) => {
      const response = await apiClient.post<InstructionHandbookDocument>(
        `/machinery-directive-compliance/${complianceId}/instruction-handbook/versions`,
        { sections }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INSTRUCTION_HANDBOOK_DOCUMENT_KEY, complianceId],
      })
    },
  })
}

export function useStartInstructionHandbookGeneration(complianceId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    {
      onEvent?: (event: InstructionHandbookStreamEvent) => void
      onComplete?: () => void
      /** Required for full-document generation; omit for partial section regen. */
      language?: string
      /** When non-empty, server regenerates only these sections and merges into the latest manual. */
      sectionsToGenerate?: string[]
    }
  >({
    mutationFn: async ({
      onEvent,
      onComplete,
      language,
      sectionsToGenerate,
    }) => {
      const hasSections =
        sectionsToGenerate != null && sectionsToGenerate.length > 0
      const query =
        language != null && language.length > 0
          ? `?language=${encodeURIComponent(language)}`
          : ''
      const url = `${getStreamUrl(
        `/machinery-directive-compliance/${complianceId}/instruction-handbook-stream`
      )}${query}`
      const body = hasSections
        ? JSON.stringify({ sectionsToGenerate })
        : undefined
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getStreamHeaders(),
          Accept: 'text/event-stream',
          ...(body != null ? { 'Content-Type': 'application/json' } : {}),
        },
        credentials: 'include',
        body,
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start instruction handbook generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            type StreamPayload =
              | InstructionHandbookStreamEvent
              | { type: 'workflow.failed'; error?: string }
            const event = JSON.parse(data) as StreamPayload
            if (
              event.type?.startsWith('document_section.') ||
              event.type === 'document.completed' ||
              event.type === 'error'
            ) {
              onEvent?.(event as InstructionHandbookStreamEvent)
            }
            if (event.type === 'document.completed') {
              onComplete?.()
              return
            }
            if (event.type === 'error') {
              throw new Error(
                (event as InstructionHandbookStreamEvent & { error?: string })
                  .error ?? 'Generation failed'
              )
            }
            if (event.type === 'workflow.failed') {
              throw new Error(event.error ?? 'Workflow failed')
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INSTRUCTION_HANDBOOK_DOCUMENT_KEY, complianceId],
      })
    },
  })
}

export function useExportInstructionHandbookPdf(complianceId: string) {
  return useMutation<void, Error, ExportCompliancePdfPayload>({
    mutationFn: async (payload) => {
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/machinery-directive-compliance/${complianceId}/instruction-handbook/export-pdf`
      const exportFilename = buildInstructionHandbookPdfFilename(new Date())
      await downloadExportPost(
        url,
        payload,
        exportFilename,
        'No instruction handbook document found to export'
      )
    },
  })
}

// --- Sistema SSM export ---

export function useExportSistemaSsm(complianceId: string | undefined) {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/machinery-directive-compliance/${complianceId}/safety-functions/sistema-export`
      await downloadExport(
        url,
        'safety-functions.ssm',
        'No safety functions found to export'
      )
    },
  })
}

export function useExportSrsPdf(complianceId: string | undefined) {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/machinery-directive-compliance/${complianceId}/export-srs-pdf`
      await downloadExport(
        url,
        'safety-requirements-specification.pdf',
        'No safety functions found to export'
      )
    },
  })
}

export type DeclarationOfConformityDocumentResponse = {
  markdown: string
  declarationOfConformityUpdatedAt: string
}

export function useGenerateDeclarationOfConformity(
  complianceId: string | undefined
) {
  const queryClient = useQueryClient()
  return useMutation<DeclarationOfConformityDocumentResponse, Error, void>({
    mutationFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response =
        await apiClient.post<DeclarationOfConformityDocumentResponse>(
          `/machinery-directive-compliance/${complianceId}/machinery-directive-declaration-of-conformity/generate`
        )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData<MachineryDirectiveCompliance>(
        [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
        (prev) =>
          prev
            ? {
                ...prev,
                declarationOfConformityMarkdown: data.markdown,
                declarationOfConformityUpdatedAt:
                  data.declarationOfConformityUpdatedAt,
              }
            : prev
      )
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

export function usePatchDeclarationOfConformityMarkdown(
  complianceId: string | undefined
) {
  const queryClient = useQueryClient()
  return useMutation<
    DeclarationOfConformityDocumentResponse,
    Error,
    { markdown: string }
  >({
    mutationFn: async ({ markdown }) => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response =
        await apiClient.patch<DeclarationOfConformityDocumentResponse>(
          `/machinery-directive-compliance/${complianceId}/machinery-directive-declaration-of-conformity`,
          { markdown }
        )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData<MachineryDirectiveCompliance>(
        [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
        (prev) =>
          prev
            ? {
                ...prev,
                declarationOfConformityMarkdown: data.markdown,
                declarationOfConformityUpdatedAt:
                  data.declarationOfConformityUpdatedAt,
              }
            : prev
      )
      void queryClient.invalidateQueries({
        queryKey: [MACHINERY_DIRECTIVE_COMPLIANCE_KEY, complianceId],
      })
    },
  })
}

async function downloadDeclarationOfConformityPdf(url: string): Promise<void> {
  const headers = {
    ...apiClient.defaults.headers.common,
  } as Record<string, string>
  delete headers['Content-Type']
  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  })
  if (!response.ok) {
    let message = `Export failed: ${response.statusText}`
    try {
      const body = (await response.json()) as { message?: string }
      if (typeof body?.message === 'string' && body.message.length > 0) {
        message = body.message
      }
    } catch {
      // ignore JSON parse errors
    }
    throw new Error(message)
  }
  const blob = await response.blob()
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = 'machinery-directive-declaration-of-conformity.pdf'
  link.click()
  URL.revokeObjectURL(downloadUrl)
}

export function useExportMachineryDirectiveDeclarationOfConformityPdf(
  complianceId: string | undefined
) {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/machinery-directive-compliance/${complianceId}/machinery-directive-declaration-of-conformity/export-pdf`
      await downloadDeclarationOfConformityPdf(url)
    },
  })
}

// --- Manual validation (manual validation page) ---

/**
 * Reserved standard ID for Machinery Directive Annex I 1.7.4 requirements.
 * Used when validating against the built-in Machinery Directive criteria (not a user standard).
 * Must match server MACHINERY_DIRECTIVE_STANDARD_ID.
 */
export const MACHINERY_DIRECTIVE_STANDARD_ID = 'machinery-directive'

const MACHINERY_DIRECTIVE_REQUIREMENTS_KEY = 'machineryDirectiveRequirements'

export function useMachineryDirectiveRequirements(
  complianceId: string | undefined
) {
  return useQuery<ManualStandardItem[], Error>({
    queryKey: [MACHINERY_DIRECTIVE_REQUIREMENTS_KEY, complianceId],
    queryFn: async () => {
      if (!complianceId) {
        throw new Error('Compliance ID is required')
      }
      const response = await apiClient.get<
        Array<{ id: string; number: string; text: string }>
      >(
        `/machinery-directive-compliance/${complianceId}/machinery-directive-requirements`
      )
      return response.data.map((item) => ({
        id: item.id,
        number: item.number,
        text: item.text,
      }))
    },
    enabled: !!complianceId,
  })
}

/** Single clause row for manual validation (flat list; no nested items). */
export type ManualStandardRequirementItem = {
  /** Stable identifier (clause id for user standards; directive index for Annex I 1.7.4). */
  id: string
  text: string
  /** Clause number from the standard (e.g. "7.2", "7.2.a") for display. */
  number: string
}

/** Display item: one clause in the information-for-use list. */
export type ManualStandardItem = ManualStandardRequirementItem

/** Validation result from stream; number may be omitted. */
export type ManualStandardRequirementItemWithValidation = Omit<
  ManualStandardRequirementItem,
  'number'
> & {
  number?: string
  verified: boolean
  explanation: string
}

export type ManualStandardRequirementsEntry = {
  standard: string
  items: ManualStandardItem[]
}
